// Web CRM (Business plane) ANALYTICS i18n dictionary — STATIC UI CHROME ONLY.
// Merged into the flat per-language lookup by ./translations.js (same t() semantics, no new dep).
//
// SCOPE: visible static labels rendered by components/Business/Analytics.jsx (tabs, card titles,
// stat labels, buttons, dialog titles/descriptions, placeholders, empty-states, toast title/desc).
// NEVER used for dynamic/backend/user content (metric values, counts, %, dates, names, brand IDs,
// API enums, err.detail). Common buttons/status reuse the `common.*` / `nav.*` keys — not redefined.
//
// English is the default + fallback. Every language below carries the IDENTICAL key set.
// Interpolation tokens: {format} {range} {count} {rate} (single-brace, per translations.js t()).

const analyticsTranslations = {
  en: {
    // ---- Toasts ----
    'an.toast.exportStarted.title': "Export Started",
    'an.toast.exportStarted.desc': "Generating board-ready {format} report...",
    'an.toast.exportComplete.title': "Export Complete",
    'an.toast.exportComplete.desc': "Board-ready {format} report has been downloaded",
    'an.toast.exportFailed.title': "Export Failed",
    'an.toast.exportFailed.desc': "Failed to generate report. Please try again.",
    'an.toast.dateRangeUpdated.title': "Date Range Updated",
    'an.toast.dateRangeUpdated.desc': "Showing analytics for {range} period",
    'an.toast.filtersApplied.title': "Filters Applied",
    'an.toast.filtersApplied.desc': "Analytics data has been filtered according to your criteria",
    'an.toast.filtersReset.title': "Filters Reset",
    'an.toast.filtersReset.desc': "All filters have been cleared",
    'an.toast.dataRefreshed.title': "Data Refreshed",
    'an.toast.refreshFailed.title': "Refresh Failed",
    'an.toast.escalationRefreshed.desc': "Escalation data updated.",
    'an.toast.escalationRefreshFailed.desc': "Could not refresh escalation data.",
    'an.toast.confidenceRefreshed.desc': "Confidence data updated.",
    'an.toast.confidenceRefreshFailed.desc': "Could not refresh confidence data.",
    // ---- Header ----
    'an.sampleBanner': "Live analytics could not be loaded right now. Showing available real data only; figures may be incomplete until the connection recovers.",
    'an.header.title': "Analytics Dashboard",
    'an.header.subtitle': "Comprehensive insights and performance metrics",
    // ---- Date range dialog ----
    'an.dateRange.button': "Date Range",
    'an.dateRange.dialogTitle': "Select Date Range",
    'an.dateRange.dialogDesc': "Choose the time period for analytics",
    'an.dateRange.customRange': "Custom Range",
    'an.dateRange.apply': "Apply Range",
    'an.dateRange.invalid': "Select a start and end date. The end date cannot be before the start date.",
    'an.dateRange.from': "From",
    'an.dateRange.to': "To",
    // ---- Period selector ----
    'an.period.day': "Today",
    'an.period.week': "This Week",
    'an.period.month': "This Month",
    'an.period.quarter': "This Quarter",
    'an.period.year': "This Year",
    // ---- Filters dialog ----
    'an.filters.dialogTitle': "Advanced Filters",
    'an.filters.dialogDesc': "Customize your analytics view",
    'an.filters.ticketCategory': "Ticket Category",
    'an.filters.searchPlaceholder': "Search analytics...",
    'an.filters.cat.all': "All Categories",
    'an.filters.cat.technical': "Technical Support",
    'an.filters.cat.billing': "Billing",
    'an.filters.cat.product': "Product Issues",
    'an.filters.cat.delivery': "Delivery",
    'an.filters.statusLabel': "Status",
    'an.filters.status.all': "All Statuses",
    'an.filters.status.open': "Open",
    'an.filters.status.inProgress': "In Progress",
    'an.filters.status.resolved': "Resolved",
    'an.filters.status.closed': "Closed",
    'an.filters.priorityLabel': "Priority",
    'an.filters.priority.all': "All Priorities",
    'an.filters.priority.low': "Low",
    'an.filters.priority.medium': "Medium",
    'an.filters.priority.high': "High",
    'an.filters.priority.urgent': "Urgent",
    'an.filters.reset': "Reset Filters",
    'an.filters.apply': "Apply Filters",
    // ---- Export dialog ----
    'an.export.button': "Export",
    'an.export.dialogTitle': "Export Board-Ready Analytics Report",
    'an.export.dialogDesc': "Generate professional reports for executive presentations with charts, KPIs, and summaries",
    'an.export.pdf': "Executive PDF",
    'an.export.pdfSub': "Board-ready report",
    'an.export.excel': "Excel Analysis",
    'an.export.excelSub': "Charts & pivot tables",
    'an.export.ppt': "PowerPoint",
    'an.export.success': 'Export complete',
    'an.aiMetricsUnavailable': 'AI metrics are currently unavailable — AI-derived figures below may be incomplete. Try Refresh.',
    'an.export.pptSub': "Presentation slides",
    'an.export.csv': "Raw Data CSV",
    'an.export.csvSub': "Data analysis",
    'an.export.generating': "Generating report...",
    'an.export.pdfReport': "PDF Report",
    'an.export.excelShort': "Excel",
    'an.export.csvShort': "CSV",
    // ---- Tabs ----
    'an.tab.overview': "Overview",
    'an.tab.categories': "Categories",
    'an.tab.aiVoice': "AI Voice",
    'an.tab.aiAnalytics': "AI Analytics",
    'an.tab.escalation': "Escalation",
    'an.tab.confidence': "Confidence",
    'an.tab.performance': "Performance",
    'an.tab.customer': "Customer",
    'an.tab.operational': "Operational",
    'an.tab.strategic': "Strategic",
    // ---- Shared stat labels ----
    'an.stat.totalConversations': "Total Conversations",
    'an.stat.autoResolutionRate': "Auto-Resolution Rate",
    'an.stat.avgFirstResponse': "Avg First Response",
    'an.stat.customerSatisfaction': "Customer Satisfaction",
    'an.stat.avgResolutionTime': "Avg Resolution Time",
    'an.stat.avgChatDuration': "Avg Chat Duration",
    'an.stat.escalationRate': "Escalation Rate",
    'an.stat.totalTickets': "Total Tickets",
    'an.stat.resolvedTickets': "Resolved Tickets",
    'an.stat.reopenedTickets': "Reopened Tickets",
    'an.stat.callbackRate': "Callback Rate",
    'an.stat.reopenedConversations': "Reopened Conversations",
    'an.stat.openaiUsageRate': "OpenAI Usage Rate",
    'an.stat.totalCalls': "Total Calls",
    'an.stat.totalCategories': "Total Categories",
    'an.stat.resolutionRate': "Resolution Rate",
    // ---- Reusable labels / chart series ----
    'an.label.resolved': "Resolved",
    'an.label.pending': "Pending",
    'an.label.escalated': "Escalated",
    'an.label.tickets': "Tickets",
    'an.label.ticket': "ticket",
    // ---- Values ----
    'an.value.noRatings': "No ratings yet",
    'an.value.responses': "{count} responses",
    // ---- Chart / card titles ----
    'an.chart.weeklyPerformanceTrend': "Weekly Performance Trend",
    'an.chart.categoryDistribution': "Tickets by Category Distribution",
    'an.chart.dailyTicketTrend': "Daily Ticket Trend",
    'an.chart.ticketsByCategory': "Tickets by Category",
    'an.chart.conversationIntents': "Conversation Intents",
    'an.chart.weeklyResolutionTrends': "Weekly Resolution Trends",
    'an.chart.topIssues': "Top Issues This Period",
    'an.chart.categoryResolutionRate': "Category Resolution Rate",
    'an.chart.resolutionRateByCategory': "Resolution Rate by Category",
    'an.chart.categoryBreakdown': "Category Breakdown",
    'an.chart.customerLanguages': "Customer Languages",
    'an.chart.topConversationIntents': "Top Conversation Intents",
    'an.chart.dailyTicketVolume': "Daily Ticket Volume",
    'an.chart.resolutionVsEscalation': "Resolution vs Escalation Trend",
    // ---- Extra series names ----
    'an.series.resolutionPct': "Resolution %",
    'an.series.escalations': "Escalations",
    // ---- Empty states ----
    'an.empty.noTicketData': "No ticket data in the selected period/filters.",
    'an.empty.noTicketActivity': "No ticket activity in the selected period.",
    'an.empty.noIntentData': "No conversation intent data available yet.",
    'an.empty.notEnoughBucket': "Not enough data to bucket resolution rates.",
    'an.empty.noLanguageData': "No language data available yet.",
    'an.empty.noTrendData': "No trend data in the selected period.",
    'an.empty.noCategoryData.title': "No ticket category data for the selected period or filters.",
    'an.empty.noCategoryData.desc': "Once tickets exist in this period, category breakdowns appear here automatically.",
    // ---- Resolution buckets ----
    'an.bucket.excellent': "Excellent (>90%)",
    'an.bucket.good': "Good (70-90%)",
    'an.bucket.needsImprovement': "Needs Improvement (<70%)",
    // ---- IT-unavailable state ----
    'an.it.title': "Ticket-level analytics aren't available for the IT/Integration role.",
    'an.it.desc': "Use the Overview, AI Voice, AI Analytics, Escalation and Confidence tabs for IT-permitted metrics.",
    // ---- Category breakdown table ----
    'an.table.category': "Category",
    'an.table.rate': "Rate",
    // ---- Performance tab ----
    'an.perf.loading': "Live performance metrics are loading or unavailable for the selected period.",
    'an.perf.aiPerformance': "AI Performance",
    'an.perf.ticketOperations': "Ticket Operations",
    'an.perf.qualityMetrics': "Quality Metrics",
    // ---- Operational tab ----
    'an.op.loading': "Live operational metrics are loading or unavailable for the selected period.",
    // ---- Strategic tab ----
    'an.strat.loading': "Live strategic metrics are loading or unavailable for the selected period.",
    'an.strat.aiAutoResolution': "AI Auto-Resolution",
    'an.strat.resolvedWithoutStaff': "Tickets resolved without staff",
    // ---- Escalation tab ----
    'an.esc.heading': "Escalation Rate Analytics",
    'an.esc.subheading': "AI conversation escalation metrics and insights",
    'an.esc.exportTitle': "Export Escalation Metrics",
    'an.esc.exportDesc': "Download escalation analytics in your preferred format",
    'an.esc.overview': "Escalation Overview",
    'an.esc.totalEscalations': "Total Escalations",
    'an.esc.byLevel': "Escalation by Level",
    'an.esc.escalationsCount': "{count} escalations",
    'an.esc.noData': "No escalation data available",
    'an.esc.insights': "Escalation Insights",
    'an.esc.impact': "Escalation Impact",
    'an.esc.impactDesc': "{count} conversations required human intervention",
    'an.esc.autoSuccess': "Auto-Resolution Success",
    'an.esc.autoSuccessDesc': "{count} conversations resolved automatically",
    'an.esc.note': "Lower escalation rates indicate better AI performance and solution coverage",
    'an.esc.distribution': "Escalation Distribution",
    // ---- Refresh ----
    'an.refreshData': "Refresh Data",
    // ---- Confidence tab ----
    'an.conf.heading': "AI Confidence Analytics",
    'an.conf.subheading': "AI decision confidence metrics and quality indicators",
    'an.conf.exportTitle': "Export Confidence Metrics",
    'an.conf.exportDesc': "Download confidence analytics in your preferred format",
    'an.conf.overview': "Confidence Overview",
    'an.conf.averageAiConfidence': "Average AI Confidence",
    'an.conf.highGt80': "High Confidence (>80%)",
    'an.conf.notCaptured': "Confidence not captured for these interactions",
    'an.conf.metrics': "Confidence Metrics",
    'an.conf.high': "High Confidence",
    'an.conf.above80': "Above 80%",
    'an.conf.medium': "Medium Confidence",
    'an.conf.range5080': "50-80%",
    'an.conf.low': "Low Confidence",
    'an.conf.below50': "Below 50%",
    'an.conf.conversationsUnit': "conversations",
    'an.conf.impact': "Confidence Impact",
    'an.conf.aiPerfDesc': "Higher confidence scores indicate better AI training and solution quality",
    'an.conf.resolutionQuality': "Resolution Quality",
    'an.conf.resolutionQualityDesc': "{rate}% of conversations resolved with confidence",
    'an.conf.note': "Confidence scores reflect the AI's certainty in its intent detection and solution matching",
    'an.conf.distribution': "Confidence Level Distribution",
    'an.conf.mediumRange': "Medium Confidence (50-80%)",
    'an.conf.lowLt50': "Low Confidence (<50%)",
    'an.conf.bandUnavailable': "Per-conversation confidence bands are not tracked — only the average AI confidence above is available.",
  },

  es: {
    'an.toast.exportStarted.title': "Exportación iniciada",
    'an.toast.exportStarted.desc': "Generando informe {format} listo para la junta...",
    'an.toast.exportComplete.title': "Exportación completa",
    'an.toast.exportComplete.desc': "El informe {format} listo para la junta se ha descargado",
    'an.toast.exportFailed.title': "Error de exportación",
    'an.toast.exportFailed.desc': "No se pudo generar el informe. Inténtalo de nuevo.",
    'an.toast.dateRangeUpdated.title': "Rango de fechas actualizado",
    'an.toast.dateRangeUpdated.desc': "Mostrando analíticas del periodo {range}",
    'an.toast.filtersApplied.title': "Filtros aplicados",
    'an.toast.filtersApplied.desc': "Los datos analíticos se han filtrado según tus criterios",
    'an.toast.filtersReset.title': "Filtros restablecidos",
    'an.toast.filtersReset.desc': "Se han borrado todos los filtros",
    'an.toast.dataRefreshed.title': "Datos actualizados",
    'an.toast.refreshFailed.title': "Error al actualizar",
    'an.toast.escalationRefreshed.desc': "Datos de escalado actualizados.",
    'an.toast.escalationRefreshFailed.desc': "No se pudieron actualizar los datos de escalado.",
    'an.toast.confidenceRefreshed.desc': "Datos de confianza actualizados.",
    'an.toast.confidenceRefreshFailed.desc': "No se pudieron actualizar los datos de confianza.",
    'an.sampleBanner': "No se pudieron cargar las analíticas en vivo ahora mismo. Se muestran solo los datos reales disponibles; las cifras pueden estar incompletas hasta que se restablezca la conexión.",
    'an.header.title': "Panel de analíticas",
    'an.header.subtitle': "Perspectivas completas y métricas de rendimiento",
    'an.dateRange.button': "Rango de fechas",
    'an.dateRange.dialogTitle': "Seleccionar rango de fechas",
    'an.dateRange.dialogDesc': "Elige el periodo de tiempo para las analíticas",
    'an.dateRange.customRange': "Rango personalizado",
    'an.dateRange.apply': "Aplicar rango",
    'an.dateRange.invalid': "Seleccione una fecha de inicio y fin. La fecha final no puede ser anterior a la inicial.",
    'an.dateRange.from': "Desde",
    'an.dateRange.to': "Hasta",
    'an.period.day': "Hoy",
    'an.period.week': "Esta semana",
    'an.period.month': "Este mes",
    'an.period.quarter': "Este trimestre",
    'an.period.year': "Este año",
    'an.filters.dialogTitle': "Filtros avanzados",
    'an.filters.dialogDesc': "Personaliza tu vista de analíticas",
    'an.filters.ticketCategory': "Categoría de ticket",
    'an.filters.searchPlaceholder': "Buscar analíticas...",
    'an.filters.cat.all': "Todas las categorías",
    'an.filters.cat.technical': "Soporte técnico",
    'an.filters.cat.billing': "Facturación",
    'an.filters.cat.product': "Problemas de producto",
    'an.filters.cat.delivery': "Entrega",
    'an.filters.statusLabel': "Estado",
    'an.filters.status.all': "Todos los estados",
    'an.filters.status.open': "Abierto",
    'an.filters.status.inProgress': "En progreso",
    'an.filters.status.resolved': "Resuelto",
    'an.filters.status.closed': "Cerrado",
    'an.filters.priorityLabel': "Prioridad",
    'an.filters.priority.all': "Todas las prioridades",
    'an.filters.priority.low': "Baja",
    'an.filters.priority.medium': "Media",
    'an.filters.priority.high': "Alta",
    'an.filters.priority.urgent': "Urgente",
    'an.filters.reset': "Restablecer filtros",
    'an.filters.apply': "Aplicar filtros",
    'an.export.button': "Exportar",
    'an.export.dialogTitle': "Exportar informe de analíticas listo para la junta",
    'an.export.dialogDesc': "Genera informes profesionales para presentaciones ejecutivas con gráficos, KPI y resúmenes",
    'an.export.pdf': "PDF ejecutivo",
    'an.export.pdfSub': "Informe listo para la junta",
    'an.export.excel': "Análisis en Excel",
    'an.export.excelSub': "Gráficos y tablas dinámicas",
    'an.export.ppt': "PowerPoint",
    'an.export.success': 'Exportación completada',
    'an.aiMetricsUnavailable': 'Las métricas de IA no están disponibles: las cifras derivadas de IA pueden estar incompletas. Intente actualizar.',
    'an.export.pptSub': "Diapositivas de presentación",
    'an.export.csv': "CSV de datos brutos",
    'an.export.csvSub': "Análisis de datos",
    'an.export.generating': "Generando informe...",
    'an.export.pdfReport': "Informe PDF",
    'an.export.excelShort': "Excel",
    'an.export.csvShort': "CSV",
    'an.tab.overview': "Resumen",
    'an.tab.categories': "Categorías",
    'an.tab.aiVoice': "Voz IA",
    'an.tab.aiAnalytics': "Analíticas IA",
    'an.tab.escalation': "Escalado",
    'an.tab.confidence': "Confianza",
    'an.tab.performance': "Rendimiento",
    'an.tab.customer': "Cliente",
    'an.tab.operational': "Operativo",
    'an.tab.strategic': "Estratégico",
    'an.stat.totalConversations': "Conversaciones totales",
    'an.stat.autoResolutionRate': "Tasa de autorresolución",
    'an.stat.avgFirstResponse': "Primera respuesta media",
    'an.stat.customerSatisfaction': "Satisfacción del cliente",
    'an.stat.avgResolutionTime': "Tiempo medio de resolución",
    'an.stat.avgChatDuration': "Duración media del chat",
    'an.stat.escalationRate': "Tasa de escalado",
    'an.stat.totalTickets': "Tickets totales",
    'an.stat.resolvedTickets': "Tickets resueltos",
    'an.stat.reopenedTickets': "Tickets reabiertos",
    'an.stat.callbackRate': "Tasa de devolución de llamada",
    'an.stat.reopenedConversations': "Conversaciones reabiertas",
    'an.stat.openaiUsageRate': "Tasa de uso de OpenAI",
    'an.stat.totalCalls': "Llamadas totales",
    'an.stat.totalCategories': "Categorías totales",
    'an.stat.resolutionRate': "Tasa de resolución",
    'an.label.resolved': "Resuelto",
    'an.label.pending': "Pendiente",
    'an.label.escalated': "Escalado",
    'an.label.tickets': "Tickets",
    'an.label.ticket': "ticket",
    'an.value.noRatings': "Sin valoraciones aún",
    'an.value.responses': "{count} respuestas",
    'an.chart.weeklyPerformanceTrend': "Tendencia de rendimiento semanal",
    'an.chart.categoryDistribution': "Distribución de tickets por categoría",
    'an.chart.dailyTicketTrend': "Tendencia diaria de tickets",
    'an.chart.ticketsByCategory': "Tickets por categoría",
    'an.chart.conversationIntents': "Intenciones de conversación",
    'an.chart.weeklyResolutionTrends': "Tendencias de resolución semanal",
    'an.chart.topIssues': "Principales problemas de este periodo",
    'an.chart.categoryResolutionRate': "Tasa de resolución por categoría",
    'an.chart.resolutionRateByCategory': "Tasa de resolución por categoría",
    'an.chart.categoryBreakdown': "Desglose por categoría",
    'an.chart.customerLanguages': "Idiomas de los clientes",
    'an.chart.topConversationIntents': "Principales intenciones de conversación",
    'an.chart.dailyTicketVolume': "Volumen diario de tickets",
    'an.chart.resolutionVsEscalation': "Tendencia de resolución vs. escalado",
    'an.series.resolutionPct': "Resolución %",
    'an.series.escalations': "Escalados",
    'an.empty.noTicketData': "No hay datos de tickets en el periodo/filtros seleccionados.",
    'an.empty.noTicketActivity': "No hay actividad de tickets en el periodo seleccionado.",
    'an.empty.noIntentData': "Aún no hay datos de intención de conversación disponibles.",
    'an.empty.notEnoughBucket': "No hay datos suficientes para agrupar las tasas de resolución.",
    'an.empty.noLanguageData': "Aún no hay datos de idioma disponibles.",
    'an.empty.noTrendData': "No hay datos de tendencia en el periodo seleccionado.",
    'an.empty.noCategoryData.title': "No hay datos de categoría de tickets para el periodo o los filtros seleccionados.",
    'an.empty.noCategoryData.desc': "Cuando existan tickets en este periodo, los desgloses por categoría aparecerán aquí automáticamente.",
    'an.bucket.excellent': "Excelente (>90%)",
    'an.bucket.good': "Bueno (70-90%)",
    'an.bucket.needsImprovement': "Necesita mejorar (<70%)",
    'an.it.title': "Las analíticas a nivel de ticket no están disponibles para el rol de TI/Integración.",
    'an.it.desc': "Usa las pestañas Resumen, Voz IA, Analíticas IA, Escalado y Confianza para las métricas permitidas a TI.",
    'an.table.category': "Categoría",
    'an.table.rate': "Tasa",
    'an.perf.loading': "Las métricas de rendimiento en vivo se están cargando o no están disponibles para el periodo seleccionado.",
    'an.perf.aiPerformance': "Rendimiento de IA",
    'an.perf.ticketOperations': "Operaciones de tickets",
    'an.perf.qualityMetrics': "Métricas de calidad",
    'an.op.loading': "Las métricas operativas en vivo se están cargando o no están disponibles para el periodo seleccionado.",
    'an.strat.loading': "Las métricas estratégicas en vivo se están cargando o no están disponibles para el periodo seleccionado.",
    'an.strat.aiAutoResolution': "Autorresolución de IA",
    'an.strat.resolvedWithoutStaff': "Tickets resueltos sin personal",
    'an.esc.heading': "Analíticas de tasa de escalado",
    'an.esc.subheading': "Métricas e información sobre el escalado de conversaciones de IA",
    'an.esc.exportTitle': "Exportar métricas de escalado",
    'an.esc.exportDesc': "Descarga las analíticas de escalado en tu formato preferido",
    'an.esc.overview': "Resumen de escalado",
    'an.esc.totalEscalations': "Escalados totales",
    'an.esc.byLevel': "Escalado por nivel",
    'an.esc.escalationsCount': "{count} escalados",
    'an.esc.noData': "No hay datos de escalado disponibles",
    'an.esc.insights': "Información sobre escalado",
    'an.esc.impact': "Impacto del escalado",
    'an.esc.impactDesc': "{count} conversaciones requirieron intervención humana",
    'an.esc.autoSuccess': "Éxito de autorresolución",
    'an.esc.autoSuccessDesc': "{count} conversaciones resueltas automáticamente",
    'an.esc.note': "Tasas de escalado más bajas indican un mejor rendimiento de la IA y una mejor cobertura de soluciones",
    'an.esc.distribution': "Distribución de escalados",
    'an.refreshData': "Actualizar datos",
    'an.conf.heading': "Analíticas de confianza de IA",
    'an.conf.subheading': "Métricas de confianza de decisión de IA e indicadores de calidad",
    'an.conf.exportTitle': "Exportar métricas de confianza",
    'an.conf.exportDesc': "Descarga las analíticas de confianza en tu formato preferido",
    'an.conf.overview': "Resumen de confianza",
    'an.conf.averageAiConfidence': "Confianza media de la IA",
    'an.conf.highGt80': "Confianza alta (>80%)",
    'an.conf.metrics': "Métricas de confianza",
    'an.conf.high': "Confianza alta",
    'an.conf.above80': "Más del 80%",
    'an.conf.medium': "Confianza media",
    'an.conf.range5080': "50-80%",
    'an.conf.low': "Confianza baja",
    'an.conf.below50': "Menos del 50%",
    'an.conf.conversationsUnit': "conversaciones",
    'an.conf.impact': "Impacto de la confianza",
    'an.conf.aiPerfDesc': "Puntuaciones de confianza más altas indican un mejor entrenamiento de la IA y una mayor calidad de las soluciones",
    'an.conf.resolutionQuality': "Calidad de resolución",
    'an.conf.resolutionQualityDesc': "{rate}% de las conversaciones resueltas con confianza",
    'an.conf.note': "Las puntuaciones de confianza reflejan la certeza de la IA en su detección de intención y coincidencia de soluciones",
    'an.conf.distribution': "Distribución del nivel de confianza",
    'an.conf.mediumRange': "Confianza media (50-80%)",
    'an.conf.lowLt50': "Confianza baja (<50%)",
    'an.conf.bandUnavailable': "Las bandas de confianza por conversación no se registran; solo está disponible la confianza media de la IA que se muestra arriba.",
  },

  fr: {
    'an.toast.exportStarted.title': "Exportation lancée",
    'an.toast.exportStarted.desc': "Génération du rapport {format} prêt pour le conseil...",
    'an.toast.exportComplete.title': "Exportation terminée",
    'an.toast.exportComplete.desc': "Le rapport {format} prêt pour le conseil a été téléchargé",
    'an.toast.exportFailed.title': "Échec de l'exportation",
    'an.toast.exportFailed.desc': "Échec de la génération du rapport. Veuillez réessayer.",
    'an.toast.dateRangeUpdated.title': "Plage de dates mise à jour",
    'an.toast.dateRangeUpdated.desc': "Affichage des analyses pour la période {range}",
    'an.toast.filtersApplied.title': "Filtres appliqués",
    'an.toast.filtersApplied.desc': "Les données analytiques ont été filtrées selon vos critères",
    'an.toast.filtersReset.title': "Filtres réinitialisés",
    'an.toast.filtersReset.desc': "Tous les filtres ont été effacés",
    'an.toast.dataRefreshed.title': "Données actualisées",
    'an.toast.refreshFailed.title': "Échec de l'actualisation",
    'an.toast.escalationRefreshed.desc': "Données d'escalade mises à jour.",
    'an.toast.escalationRefreshFailed.desc': "Impossible d'actualiser les données d'escalade.",
    'an.toast.confidenceRefreshed.desc': "Données de confiance mises à jour.",
    'an.toast.confidenceRefreshFailed.desc': "Impossible d'actualiser les données de confiance.",
    'an.sampleBanner': "Les analyses en direct n'ont pas pu être chargées pour le moment. Seules les données réelles disponibles sont affichées ; les chiffres peuvent être incomplets jusqu'au rétablissement de la connexion.",
    'an.header.title': "Tableau de bord analytique",
    'an.header.subtitle': "Aperçus complets et indicateurs de performance",
    'an.dateRange.button': "Plage de dates",
    'an.dateRange.dialogTitle': "Sélectionner une plage de dates",
    'an.dateRange.dialogDesc': "Choisissez la période pour les analyses",
    'an.dateRange.customRange': "Plage personnalisée",
    'an.dateRange.apply': "Appliquer la plage",
    'an.dateRange.invalid': "Sélectionnez une date de début et de fin. La date de fin ne peut pas précéder la date de début.",
    'an.dateRange.from': "Du",
    'an.dateRange.to': "Au",
    'an.period.day': "Aujourd'hui",
    'an.period.week': "Cette semaine",
    'an.period.month': "Ce mois-ci",
    'an.period.quarter': "Ce trimestre",
    'an.period.year': "Cette année",
    'an.filters.dialogTitle': "Filtres avancés",
    'an.filters.dialogDesc': "Personnalisez votre vue analytique",
    'an.filters.ticketCategory': "Catégorie de ticket",
    'an.filters.searchPlaceholder': "Rechercher dans les analyses...",
    'an.filters.cat.all': "Toutes les catégories",
    'an.filters.cat.technical': "Support technique",
    'an.filters.cat.billing': "Facturation",
    'an.filters.cat.product': "Problèmes de produit",
    'an.filters.cat.delivery': "Livraison",
    'an.filters.statusLabel': "Statut",
    'an.filters.status.all': "Tous les statuts",
    'an.filters.status.open': "Ouvert",
    'an.filters.status.inProgress': "En cours",
    'an.filters.status.resolved': "Résolu",
    'an.filters.status.closed': "Fermé",
    'an.filters.priorityLabel': "Priorité",
    'an.filters.priority.all': "Toutes les priorités",
    'an.filters.priority.low': "Basse",
    'an.filters.priority.medium': "Moyenne",
    'an.filters.priority.high': "Haute",
    'an.filters.priority.urgent': "Urgente",
    'an.filters.reset': "Réinitialiser les filtres",
    'an.filters.apply': "Appliquer les filtres",
    'an.export.button': "Exporter",
    'an.export.dialogTitle': "Exporter un rapport analytique prêt pour le conseil",
    'an.export.dialogDesc': "Générez des rapports professionnels pour les présentations aux dirigeants avec graphiques, KPI et synthèses",
    'an.export.pdf': "PDF exécutif",
    'an.export.pdfSub': "Rapport prêt pour le conseil",
    'an.export.excel': "Analyse Excel",
    'an.export.excelSub': "Graphiques et tableaux croisés",
    'an.export.ppt': "PowerPoint",
    'an.export.success': 'Export terminé',
    'an.aiMetricsUnavailable': "Les métriques IA sont indisponibles : les chiffres issus de l'IA peuvent être incomplets. Essayez Actualiser.",
    'an.export.pptSub': "Diapositives de présentation",
    'an.export.csv': "CSV de données brutes",
    'an.export.csvSub': "Analyse de données",
    'an.export.generating': "Génération du rapport...",
    'an.export.pdfReport': "Rapport PDF",
    'an.export.excelShort': "Excel",
    'an.export.csvShort': "CSV",
    'an.tab.overview': "Vue d'ensemble",
    'an.tab.categories': "Catégories",
    'an.tab.aiVoice': "Voix IA",
    'an.tab.aiAnalytics': "Analyses IA",
    'an.tab.escalation': "Escalade",
    'an.tab.confidence': "Confiance",
    'an.tab.performance': "Performance",
    'an.tab.customer': "Client",
    'an.tab.operational': "Opérationnel",
    'an.tab.strategic': "Stratégique",
    'an.stat.totalConversations': "Conversations totales",
    'an.stat.autoResolutionRate': "Taux d'auto-résolution",
    'an.stat.avgFirstResponse': "Première réponse moyenne",
    'an.stat.customerSatisfaction': "Satisfaction client",
    'an.stat.avgResolutionTime': "Temps moyen de résolution",
    'an.stat.avgChatDuration': "Durée moyenne du chat",
    'an.stat.escalationRate': "Taux d'escalade",
    'an.stat.totalTickets': "Tickets totaux",
    'an.stat.resolvedTickets': "Tickets résolus",
    'an.stat.reopenedTickets': "Tickets rouverts",
    'an.stat.callbackRate': "Taux de rappel",
    'an.stat.reopenedConversations': "Conversations rouvertes",
    'an.stat.openaiUsageRate': "Taux d'utilisation d'OpenAI",
    'an.stat.totalCalls': "Appels totaux",
    'an.stat.totalCategories': "Catégories totales",
    'an.stat.resolutionRate': "Taux de résolution",
    'an.label.resolved': "Résolu",
    'an.label.pending': "En attente",
    'an.label.escalated': "Escaladé",
    'an.label.tickets': "Tickets",
    'an.label.ticket': "ticket",
    'an.value.noRatings': "Pas encore d'évaluations",
    'an.value.responses': "{count} réponses",
    'an.chart.weeklyPerformanceTrend': "Tendance de performance hebdomadaire",
    'an.chart.categoryDistribution': "Répartition des tickets par catégorie",
    'an.chart.dailyTicketTrend': "Tendance quotidienne des tickets",
    'an.chart.ticketsByCategory': "Tickets par catégorie",
    'an.chart.conversationIntents': "Intentions de conversation",
    'an.chart.weeklyResolutionTrends': "Tendances de résolution hebdomadaire",
    'an.chart.topIssues': "Principaux problèmes de cette période",
    'an.chart.categoryResolutionRate': "Taux de résolution par catégorie",
    'an.chart.resolutionRateByCategory': "Taux de résolution par catégorie",
    'an.chart.categoryBreakdown': "Répartition par catégorie",
    'an.chart.customerLanguages': "Langues des clients",
    'an.chart.topConversationIntents': "Principales intentions de conversation",
    'an.chart.dailyTicketVolume': "Volume quotidien de tickets",
    'an.chart.resolutionVsEscalation': "Tendance résolution vs escalade",
    'an.series.resolutionPct': "Résolution %",
    'an.series.escalations': "Escalades",
    'an.empty.noTicketData': "Aucune donnée de ticket pour la période/les filtres sélectionnés.",
    'an.empty.noTicketActivity': "Aucune activité de ticket sur la période sélectionnée.",
    'an.empty.noIntentData': "Aucune donnée d'intention de conversation disponible pour le moment.",
    'an.empty.notEnoughBucket': "Pas assez de données pour regrouper les taux de résolution.",
    'an.empty.noLanguageData': "Aucune donnée de langue disponible pour le moment.",
    'an.empty.noTrendData': "Aucune donnée de tendance sur la période sélectionnée.",
    'an.empty.noCategoryData.title': "Aucune donnée de catégorie de ticket pour la période ou les filtres sélectionnés.",
    'an.empty.noCategoryData.desc': "Dès que des tickets existent pour cette période, les répartitions par catégorie apparaissent ici automatiquement.",
    'an.bucket.excellent': "Excellent (>90%)",
    'an.bucket.good': "Bon (70-90%)",
    'an.bucket.needsImprovement': "À améliorer (<70%)",
    'an.it.title': "Les analyses au niveau des tickets ne sont pas disponibles pour le rôle IT/Intégration.",
    'an.it.desc': "Utilisez les onglets Vue d'ensemble, Voix IA, Analyses IA, Escalade et Confiance pour les indicateurs autorisés au rôle IT.",
    'an.table.category': "Catégorie",
    'an.table.rate': "Taux",
    'an.perf.loading': "Les indicateurs de performance en direct se chargent ou sont indisponibles pour la période sélectionnée.",
    'an.perf.aiPerformance': "Performance de l'IA",
    'an.perf.ticketOperations': "Opérations sur les tickets",
    'an.perf.qualityMetrics': "Indicateurs de qualité",
    'an.op.loading': "Les indicateurs opérationnels en direct se chargent ou sont indisponibles pour la période sélectionnée.",
    'an.strat.loading': "Les indicateurs stratégiques en direct se chargent ou sont indisponibles pour la période sélectionnée.",
    'an.strat.aiAutoResolution': "Auto-résolution par IA",
    'an.strat.resolvedWithoutStaff': "Tickets résolus sans personnel",
    'an.esc.heading': "Analyses du taux d'escalade",
    'an.esc.subheading': "Indicateurs et informations sur l'escalade des conversations IA",
    'an.esc.exportTitle': "Exporter les indicateurs d'escalade",
    'an.esc.exportDesc': "Téléchargez les analyses d'escalade dans le format de votre choix",
    'an.esc.overview': "Aperçu de l'escalade",
    'an.esc.totalEscalations': "Escalades totales",
    'an.esc.byLevel': "Escalade par niveau",
    'an.esc.escalationsCount': "{count} escalades",
    'an.esc.noData': "Aucune donnée d'escalade disponible",
    'an.esc.insights': "Informations sur l'escalade",
    'an.esc.impact': "Impact de l'escalade",
    'an.esc.impactDesc': "{count} conversations ont nécessité une intervention humaine",
    'an.esc.autoSuccess': "Succès de l'auto-résolution",
    'an.esc.autoSuccessDesc': "{count} conversations résolues automatiquement",
    'an.esc.note': "Des taux d'escalade plus faibles indiquent une meilleure performance de l'IA et une meilleure couverture des solutions",
    'an.esc.distribution': "Répartition des escalades",
    'an.refreshData': "Actualiser les données",
    'an.conf.heading': "Analyses de confiance de l'IA",
    'an.conf.subheading': "Indicateurs de confiance des décisions de l'IA et indicateurs de qualité",
    'an.conf.exportTitle': "Exporter les indicateurs de confiance",
    'an.conf.exportDesc': "Téléchargez les analyses de confiance dans le format de votre choix",
    'an.conf.overview': "Aperçu de la confiance",
    'an.conf.averageAiConfidence': "Confiance moyenne de l'IA",
    'an.conf.highGt80': "Confiance élevée (>80%)",
    'an.conf.metrics': "Indicateurs de confiance",
    'an.conf.high': "Confiance élevée",
    'an.conf.above80': "Plus de 80%",
    'an.conf.medium': "Confiance moyenne",
    'an.conf.range5080': "50-80%",
    'an.conf.low': "Confiance faible",
    'an.conf.below50': "Moins de 50%",
    'an.conf.conversationsUnit': "conversations",
    'an.conf.impact': "Impact de la confiance",
    'an.conf.aiPerfDesc': "Des scores de confiance plus élevés indiquent un meilleur entraînement de l'IA et une meilleure qualité des solutions",
    'an.conf.resolutionQuality': "Qualité de résolution",
    'an.conf.resolutionQualityDesc': "{rate}% des conversations résolues avec confiance",
    'an.conf.note': "Les scores de confiance reflètent la certitude de l'IA dans sa détection d'intention et sa correspondance de solutions",
    'an.conf.distribution': "Répartition des niveaux de confiance",
    'an.conf.mediumRange': "Confiance moyenne (50-80%)",
    'an.conf.lowLt50': "Confiance faible (<50%)",
    'an.conf.bandUnavailable': "Les niveaux de confiance par conversation ne sont pas suivis — seule la confiance moyenne de l'IA ci-dessus est disponible.",
  },

  pt: {
    'an.toast.exportStarted.title': "Exportação iniciada",
    'an.toast.exportStarted.desc': "Gerando relatório {format} pronto para a diretoria...",
    'an.toast.exportComplete.title': "Exportação concluída",
    'an.toast.exportComplete.desc': "O relatório {format} pronto para a diretoria foi baixado",
    'an.toast.exportFailed.title': "Falha na exportação",
    'an.toast.exportFailed.desc': "Falha ao gerar o relatório. Tente novamente.",
    'an.toast.dateRangeUpdated.title': "Intervalo de datas atualizado",
    'an.toast.dateRangeUpdated.desc': "Mostrando análises do período {range}",
    'an.toast.filtersApplied.title': "Filtros aplicados",
    'an.toast.filtersApplied.desc': "Os dados de análise foram filtrados de acordo com seus critérios",
    'an.toast.filtersReset.title': "Filtros redefinidos",
    'an.toast.filtersReset.desc': "Todos os filtros foram limpos",
    'an.toast.dataRefreshed.title': "Dados atualizados",
    'an.toast.refreshFailed.title': "Falha na atualização",
    'an.toast.escalationRefreshed.desc': "Dados de escalonamento atualizados.",
    'an.toast.escalationRefreshFailed.desc': "Não foi possível atualizar os dados de escalonamento.",
    'an.toast.confidenceRefreshed.desc': "Dados de confiança atualizados.",
    'an.toast.confidenceRefreshFailed.desc': "Não foi possível atualizar os dados de confiança.",
    'an.sampleBanner': "As análises em tempo real não puderam ser carregadas agora. Mostrando apenas os dados reais disponíveis; os números podem estar incompletos até a conexão ser restabelecida.",
    'an.header.title': "Painel de análises",
    'an.header.subtitle': "Insights abrangentes e métricas de desempenho",
    'an.dateRange.button': "Intervalo de datas",
    'an.dateRange.dialogTitle': "Selecionar intervalo de datas",
    'an.dateRange.dialogDesc': "Escolha o período de tempo para as análises",
    'an.dateRange.customRange': "Intervalo personalizado",
    'an.dateRange.apply': "Aplicar intervalo",
    'an.dateRange.invalid': "Selecione uma data de início e fim. A data final não pode ser anterior à inicial.",
    'an.dateRange.from': "De",
    'an.dateRange.to': "Até",
    'an.period.day': "Hoje",
    'an.period.week': "Esta semana",
    'an.period.month': "Este mês",
    'an.period.quarter': "Este trimestre",
    'an.period.year': "Este ano",
    'an.filters.dialogTitle': "Filtros avançados",
    'an.filters.dialogDesc': "Personalize sua visualização de análises",
    'an.filters.ticketCategory': "Categoria do ticket",
    'an.filters.searchPlaceholder': "Pesquisar análises...",
    'an.filters.cat.all': "Todas as categorias",
    'an.filters.cat.technical': "Suporte técnico",
    'an.filters.cat.billing': "Faturamento",
    'an.filters.cat.product': "Problemas de produto",
    'an.filters.cat.delivery': "Entrega",
    'an.filters.statusLabel': "Status",
    'an.filters.status.all': "Todos os status",
    'an.filters.status.open': "Aberto",
    'an.filters.status.inProgress': "Em andamento",
    'an.filters.status.resolved': "Resolvido",
    'an.filters.status.closed': "Fechado",
    'an.filters.priorityLabel': "Prioridade",
    'an.filters.priority.all': "Todas as prioridades",
    'an.filters.priority.low': "Baixa",
    'an.filters.priority.medium': "Média",
    'an.filters.priority.high': "Alta",
    'an.filters.priority.urgent': "Urgente",
    'an.filters.reset': "Redefinir filtros",
    'an.filters.apply': "Aplicar filtros",
    'an.export.button': "Exportar",
    'an.export.dialogTitle': "Exportar relatório de análises pronto para a diretoria",
    'an.export.dialogDesc': "Gere relatórios profissionais para apresentações executivas com gráficos, KPIs e resumos",
    'an.export.pdf': "PDF executivo",
    'an.export.pdfSub': "Relatório pronto para a diretoria",
    'an.export.excel': "Análise em Excel",
    'an.export.excelSub': "Gráficos e tabelas dinâmicas",
    'an.export.ppt': "PowerPoint",
    'an.export.success': 'Exportação concluída',
    'an.aiMetricsUnavailable': 'As métricas de IA estão indisponíveis — os números derivados de IA podem estar incompletos. Tente atualizar.',
    'an.export.pptSub': "Slides de apresentação",
    'an.export.csv': "CSV de dados brutos",
    'an.export.csvSub': "Análise de dados",
    'an.export.generating': "Gerando relatório...",
    'an.export.pdfReport': "Relatório PDF",
    'an.export.excelShort': "Excel",
    'an.export.csvShort': "CSV",
    'an.tab.overview': "Visão geral",
    'an.tab.categories': "Categorias",
    'an.tab.aiVoice': "Voz IA",
    'an.tab.aiAnalytics': "Análises IA",
    'an.tab.escalation': "Escalonamento",
    'an.tab.confidence': "Confiança",
    'an.tab.performance': "Desempenho",
    'an.tab.customer': "Cliente",
    'an.tab.operational': "Operacional",
    'an.tab.strategic': "Estratégico",
    'an.stat.totalConversations': "Total de conversas",
    'an.stat.autoResolutionRate': "Taxa de autorresolução",
    'an.stat.avgFirstResponse': "Primeira resposta média",
    'an.stat.customerSatisfaction': "Satisfação do cliente",
    'an.stat.avgResolutionTime': "Tempo médio de resolução",
    'an.stat.avgChatDuration': "Duração média do chat",
    'an.stat.escalationRate': "Taxa de escalonamento",
    'an.stat.totalTickets': "Total de tickets",
    'an.stat.resolvedTickets': "Tickets resolvidos",
    'an.stat.reopenedTickets': "Tickets reabertos",
    'an.stat.callbackRate': "Taxa de retorno de chamada",
    'an.stat.reopenedConversations': "Conversas reabertas",
    'an.stat.openaiUsageRate': "Taxa de uso da OpenAI",
    'an.stat.totalCalls': "Total de chamadas",
    'an.stat.totalCategories': "Total de categorias",
    'an.stat.resolutionRate': "Taxa de resolução",
    'an.label.resolved': "Resolvido",
    'an.label.pending': "Pendente",
    'an.label.escalated': "Escalonado",
    'an.label.tickets': "Tickets",
    'an.label.ticket': "ticket",
    'an.value.noRatings': "Ainda sem avaliações",
    'an.value.responses': "{count} respostas",
    'an.chart.weeklyPerformanceTrend': "Tendência de desempenho semanal",
    'an.chart.categoryDistribution': "Distribuição de tickets por categoria",
    'an.chart.dailyTicketTrend': "Tendência diária de tickets",
    'an.chart.ticketsByCategory': "Tickets por categoria",
    'an.chart.conversationIntents': "Intenções de conversa",
    'an.chart.weeklyResolutionTrends': "Tendências de resolução semanal",
    'an.chart.topIssues': "Principais problemas deste período",
    'an.chart.categoryResolutionRate': "Taxa de resolução por categoria",
    'an.chart.resolutionRateByCategory': "Taxa de resolução por categoria",
    'an.chart.categoryBreakdown': "Detalhamento por categoria",
    'an.chart.customerLanguages': "Idiomas dos clientes",
    'an.chart.topConversationIntents': "Principais intenções de conversa",
    'an.chart.dailyTicketVolume': "Volume diário de tickets",
    'an.chart.resolutionVsEscalation': "Tendência de resolução vs. escalonamento",
    'an.series.resolutionPct': "Resolução %",
    'an.series.escalations': "Escalonamentos",
    'an.empty.noTicketData': "Nenhum dado de ticket no período/filtros selecionados.",
    'an.empty.noTicketActivity': "Nenhuma atividade de ticket no período selecionado.",
    'an.empty.noIntentData': "Ainda não há dados de intenção de conversa disponíveis.",
    'an.empty.notEnoughBucket': "Dados insuficientes para agrupar as taxas de resolução.",
    'an.empty.noLanguageData': "Ainda não há dados de idioma disponíveis.",
    'an.empty.noTrendData': "Nenhum dado de tendência no período selecionado.",
    'an.empty.noCategoryData.title': "Nenhum dado de categoria de ticket para o período ou filtros selecionados.",
    'an.empty.noCategoryData.desc': "Quando houver tickets neste período, os detalhamentos por categoria aparecerão aqui automaticamente.",
    'an.bucket.excellent': "Excelente (>90%)",
    'an.bucket.good': "Bom (70-90%)",
    'an.bucket.needsImprovement': "Precisa melhorar (<70%)",
    'an.it.title': "As análises no nível de ticket não estão disponíveis para a função de TI/Integração.",
    'an.it.desc': "Use as abas Visão geral, Voz IA, Análises IA, Escalonamento e Confiança para as métricas permitidas à TI.",
    'an.table.category': "Categoria",
    'an.table.rate': "Taxa",
    'an.perf.loading': "As métricas de desempenho em tempo real estão carregando ou indisponíveis para o período selecionado.",
    'an.perf.aiPerformance': "Desempenho da IA",
    'an.perf.ticketOperations': "Operações de tickets",
    'an.perf.qualityMetrics': "Métricas de qualidade",
    'an.op.loading': "As métricas operacionais em tempo real estão carregando ou indisponíveis para o período selecionado.",
    'an.strat.loading': "As métricas estratégicas em tempo real estão carregando ou indisponíveis para o período selecionado.",
    'an.strat.aiAutoResolution': "Autorresolução por IA",
    'an.strat.resolvedWithoutStaff': "Tickets resolvidos sem equipe",
    'an.esc.heading': "Análises da taxa de escalonamento",
    'an.esc.subheading': "Métricas e insights sobre o escalonamento de conversas de IA",
    'an.esc.exportTitle': "Exportar métricas de escalonamento",
    'an.esc.exportDesc': "Baixe as análises de escalonamento no formato de sua preferência",
    'an.esc.overview': "Visão geral de escalonamento",
    'an.esc.totalEscalations': "Total de escalonamentos",
    'an.esc.byLevel': "Escalonamento por nível",
    'an.esc.escalationsCount': "{count} escalonamentos",
    'an.esc.noData': "Nenhum dado de escalonamento disponível",
    'an.esc.insights': "Insights de escalonamento",
    'an.esc.impact': "Impacto do escalonamento",
    'an.esc.impactDesc': "{count} conversas exigiram intervenção humana",
    'an.esc.autoSuccess': "Sucesso da autorresolução",
    'an.esc.autoSuccessDesc': "{count} conversas resolvidas automaticamente",
    'an.esc.note': "Taxas de escalonamento mais baixas indicam melhor desempenho da IA e melhor cobertura de soluções",
    'an.esc.distribution': "Distribuição de escalonamentos",
    'an.refreshData': "Atualizar dados",
    'an.conf.heading': "Análises de confiança da IA",
    'an.conf.subheading': "Métricas de confiança das decisões da IA e indicadores de qualidade",
    'an.conf.exportTitle': "Exportar métricas de confiança",
    'an.conf.exportDesc': "Baixe as análises de confiança no formato de sua preferência",
    'an.conf.overview': "Visão geral da confiança",
    'an.conf.averageAiConfidence': "Confiança média da IA",
    'an.conf.highGt80': "Confiança alta (>80%)",
    'an.conf.metrics': "Métricas de confiança",
    'an.conf.high': "Confiança alta",
    'an.conf.above80': "Acima de 80%",
    'an.conf.medium': "Confiança média",
    'an.conf.range5080': "50-80%",
    'an.conf.low': "Confiança baixa",
    'an.conf.below50': "Abaixo de 50%",
    'an.conf.conversationsUnit': "conversas",
    'an.conf.impact': "Impacto da confiança",
    'an.conf.aiPerfDesc': "Pontuações de confiança mais altas indicam melhor treinamento da IA e maior qualidade das soluções",
    'an.conf.resolutionQuality': "Qualidade da resolução",
    'an.conf.resolutionQualityDesc': "{rate}% das conversas resolvidas com confiança",
    'an.conf.note': "As pontuações de confiança refletem a certeza da IA na detecção de intenção e na correspondência de soluções",
    'an.conf.distribution': "Distribuição do nível de confiança",
    'an.conf.mediumRange': "Confiança média (50-80%)",
    'an.conf.lowLt50': "Confiança baixa (<50%)",
    'an.conf.bandUnavailable': "As faixas de confiança por conversa não são registradas — apenas a confiança média da IA acima está disponível.",
  },

  de: {
    'an.toast.exportStarted.title': "Export gestartet",
    'an.toast.exportStarted.desc': "Vorstandstauglicher {format}-Bericht wird erstellt...",
    'an.toast.exportComplete.title': "Export abgeschlossen",
    'an.toast.exportComplete.desc': "Der vorstandstaugliche {format}-Bericht wurde heruntergeladen",
    'an.toast.exportFailed.title': "Export fehlgeschlagen",
    'an.toast.exportFailed.desc': "Bericht konnte nicht erstellt werden. Bitte erneut versuchen.",
    'an.toast.dateRangeUpdated.title': "Datumsbereich aktualisiert",
    'an.toast.dateRangeUpdated.desc': "Analysen für den Zeitraum {range} werden angezeigt",
    'an.toast.filtersApplied.title': "Filter angewendet",
    'an.toast.filtersApplied.desc': "Die Analysedaten wurden nach Ihren Kriterien gefiltert",
    'an.toast.filtersReset.title': "Filter zurückgesetzt",
    'an.toast.filtersReset.desc': "Alle Filter wurden gelöscht",
    'an.toast.dataRefreshed.title': "Daten aktualisiert",
    'an.toast.refreshFailed.title': "Aktualisierung fehlgeschlagen",
    'an.toast.escalationRefreshed.desc': "Eskalationsdaten aktualisiert.",
    'an.toast.escalationRefreshFailed.desc': "Eskalationsdaten konnten nicht aktualisiert werden.",
    'an.toast.confidenceRefreshed.desc': "Konfidenzdaten aktualisiert.",
    'an.toast.confidenceRefreshFailed.desc': "Konfidenzdaten konnten nicht aktualisiert werden.",
    'an.sampleBanner': "Live-Analysen konnten gerade nicht geladen werden. Es werden nur verfügbare Echtdaten angezeigt; die Zahlen können unvollständig sein, bis die Verbindung wiederhergestellt ist.",
    'an.header.title': "Analyse-Dashboard",
    'an.header.subtitle': "Umfassende Einblicke und Leistungskennzahlen",
    'an.dateRange.button': "Datumsbereich",
    'an.dateRange.dialogTitle': "Datumsbereich auswählen",
    'an.dateRange.dialogDesc': "Wählen Sie den Zeitraum für die Analysen",
    'an.dateRange.customRange': "Benutzerdefinierter Bereich",
    'an.dateRange.apply': "Bereich anwenden",
    'an.dateRange.invalid': "Wählen Sie ein Start- und Enddatum. Das Enddatum darf nicht vor dem Startdatum liegen.",
    'an.dateRange.from': "Von",
    'an.dateRange.to': "Bis",
    'an.period.day': "Heute",
    'an.period.week': "Diese Woche",
    'an.period.month': "Dieser Monat",
    'an.period.quarter': "Dieses Quartal",
    'an.period.year': "Dieses Jahr",
    'an.filters.dialogTitle': "Erweiterte Filter",
    'an.filters.dialogDesc': "Passen Sie Ihre Analyseansicht an",
    'an.filters.ticketCategory': "Ticketkategorie",
    'an.filters.searchPlaceholder': "Analysen durchsuchen...",
    'an.filters.cat.all': "Alle Kategorien",
    'an.filters.cat.technical': "Technischer Support",
    'an.filters.cat.billing': "Abrechnung",
    'an.filters.cat.product': "Produktprobleme",
    'an.filters.cat.delivery': "Lieferung",
    'an.filters.statusLabel': "Status",
    'an.filters.status.all': "Alle Status",
    'an.filters.status.open': "Offen",
    'an.filters.status.inProgress': "In Bearbeitung",
    'an.filters.status.resolved': "Gelöst",
    'an.filters.status.closed': "Geschlossen",
    'an.filters.priorityLabel': "Priorität",
    'an.filters.priority.all': "Alle Prioritäten",
    'an.filters.priority.low': "Niedrig",
    'an.filters.priority.medium': "Mittel",
    'an.filters.priority.high': "Hoch",
    'an.filters.priority.urgent': "Dringend",
    'an.filters.reset': "Filter zurücksetzen",
    'an.filters.apply': "Filter anwenden",
    'an.export.button': "Exportieren",
    'an.export.dialogTitle': "Vorstandstauglichen Analysebericht exportieren",
    'an.export.dialogDesc': "Erstellen Sie professionelle Berichte für Vorstandspräsentationen mit Diagrammen, KPIs und Zusammenfassungen",
    'an.export.pdf': "Executive-PDF",
    'an.export.pdfSub': "Vorstandstauglicher Bericht",
    'an.export.excel': "Excel-Analyse",
    'an.export.excelSub': "Diagramme & Pivot-Tabellen",
    'an.export.ppt': "PowerPoint",
    'an.export.success': 'Export abgeschlossen',
    'an.aiMetricsUnavailable': 'KI-Metriken sind derzeit nicht verfügbar — KI-basierte Zahlen können unvollständig sein. Bitte aktualisieren.',
    'an.export.pptSub': "Präsentationsfolien",
    'an.export.csv': "Rohdaten-CSV",
    'an.export.csvSub': "Datenanalyse",
    'an.export.generating': "Bericht wird erstellt...",
    'an.export.pdfReport': "PDF-Bericht",
    'an.export.excelShort': "Excel",
    'an.export.csvShort': "CSV",
    'an.tab.overview': "Übersicht",
    'an.tab.categories': "Kategorien",
    'an.tab.aiVoice': "KI-Sprache",
    'an.tab.aiAnalytics': "KI-Analysen",
    'an.tab.escalation': "Eskalation",
    'an.tab.confidence': "Konfidenz",
    'an.tab.performance': "Leistung",
    'an.tab.customer': "Kunde",
    'an.tab.operational': "Betrieblich",
    'an.tab.strategic': "Strategisch",
    'an.stat.totalConversations': "Gesamte Gespräche",
    'an.stat.autoResolutionRate': "Auto-Lösungsrate",
    'an.stat.avgFirstResponse': "Durchschn. Erstreaktion",
    'an.stat.customerSatisfaction': "Kundenzufriedenheit",
    'an.stat.avgResolutionTime': "Durchschn. Lösungszeit",
    'an.stat.avgChatDuration': "Durchschn. Chat-Dauer",
    'an.stat.escalationRate': "Eskalationsrate",
    'an.stat.totalTickets': "Gesamte Tickets",
    'an.stat.resolvedTickets': "Gelöste Tickets",
    'an.stat.reopenedTickets': "Wiedereröffnete Tickets",
    'an.stat.callbackRate': "Rückrufrate",
    'an.stat.reopenedConversations': "Wiedereröffnete Gespräche",
    'an.stat.openaiUsageRate': "OpenAI-Nutzungsrate",
    'an.stat.totalCalls': "Gesamte Anrufe",
    'an.stat.totalCategories': "Gesamte Kategorien",
    'an.stat.resolutionRate': "Lösungsrate",
    'an.label.resolved': "Gelöst",
    'an.label.pending': "Ausstehend",
    'an.label.escalated': "Eskaliert",
    'an.label.tickets': "Tickets",
    'an.label.ticket': "Ticket",
    'an.value.noRatings': "Noch keine Bewertungen",
    'an.value.responses': "{count} Antworten",
    'an.chart.weeklyPerformanceTrend': "Wöchentlicher Leistungstrend",
    'an.chart.categoryDistribution': "Tickets nach Kategorie – Verteilung",
    'an.chart.dailyTicketTrend': "Täglicher Ticket-Trend",
    'an.chart.ticketsByCategory': "Tickets nach Kategorie",
    'an.chart.conversationIntents': "Gesprächsabsichten",
    'an.chart.weeklyResolutionTrends': "Wöchentliche Lösungstrends",
    'an.chart.topIssues': "Häufigste Probleme in diesem Zeitraum",
    'an.chart.categoryResolutionRate': "Lösungsrate nach Kategorie",
    'an.chart.resolutionRateByCategory': "Lösungsrate nach Kategorie",
    'an.chart.categoryBreakdown': "Kategorienaufschlüsselung",
    'an.chart.customerLanguages': "Kundensprachen",
    'an.chart.topConversationIntents': "Häufigste Gesprächsabsichten",
    'an.chart.dailyTicketVolume': "Tägliches Ticketvolumen",
    'an.chart.resolutionVsEscalation': "Trend Lösung vs. Eskalation",
    'an.series.resolutionPct': "Lösung %",
    'an.series.escalations': "Eskalationen",
    'an.empty.noTicketData': "Keine Ticketdaten im ausgewählten Zeitraum/Filter.",
    'an.empty.noTicketActivity': "Keine Ticketaktivität im ausgewählten Zeitraum.",
    'an.empty.noIntentData': "Noch keine Daten zu Gesprächsabsichten verfügbar.",
    'an.empty.notEnoughBucket': "Nicht genügend Daten, um Lösungsraten zu gruppieren.",
    'an.empty.noLanguageData': "Noch keine Sprachdaten verfügbar.",
    'an.empty.noTrendData': "Keine Trenddaten im ausgewählten Zeitraum.",
    'an.empty.noCategoryData.title': "Keine Ticketkategoriedaten für den ausgewählten Zeitraum oder Filter.",
    'an.empty.noCategoryData.desc': "Sobald in diesem Zeitraum Tickets vorhanden sind, erscheinen die Kategorienaufschlüsselungen hier automatisch.",
    'an.bucket.excellent': "Ausgezeichnet (>90%)",
    'an.bucket.good': "Gut (70-90%)",
    'an.bucket.needsImprovement': "Verbesserungswürdig (<70%)",
    'an.it.title': "Analysen auf Ticketebene sind für die Rolle IT/Integration nicht verfügbar.",
    'an.it.desc': "Verwenden Sie die Registerkarten Übersicht, KI-Sprache, KI-Analysen, Eskalation und Konfidenz für die für IT zulässigen Kennzahlen.",
    'an.table.category': "Kategorie",
    'an.table.rate': "Rate",
    'an.perf.loading': "Live-Leistungskennzahlen werden geladen oder sind für den ausgewählten Zeitraum nicht verfügbar.",
    'an.perf.aiPerformance': "KI-Leistung",
    'an.perf.ticketOperations': "Ticketvorgänge",
    'an.perf.qualityMetrics': "Qualitätskennzahlen",
    'an.op.loading': "Live-Betriebskennzahlen werden geladen oder sind für den ausgewählten Zeitraum nicht verfügbar.",
    'an.strat.loading': "Live-Strategiekennzahlen werden geladen oder sind für den ausgewählten Zeitraum nicht verfügbar.",
    'an.strat.aiAutoResolution': "KI-Auto-Lösung",
    'an.strat.resolvedWithoutStaff': "Ohne Personal gelöste Tickets",
    'an.esc.heading': "Analysen der Eskalationsrate",
    'an.esc.subheading': "Kennzahlen und Einblicke zur Eskalation von KI-Gesprächen",
    'an.esc.exportTitle': "Eskalationskennzahlen exportieren",
    'an.esc.exportDesc': "Laden Sie die Eskalationsanalysen im gewünschten Format herunter",
    'an.esc.overview': "Eskalationsübersicht",
    'an.esc.totalEscalations': "Gesamte Eskalationen",
    'an.esc.byLevel': "Eskalation nach Ebene",
    'an.esc.escalationsCount': "{count} Eskalationen",
    'an.esc.noData': "Keine Eskalationsdaten verfügbar",
    'an.esc.insights': "Eskalationseinblicke",
    'an.esc.impact': "Eskalationsauswirkung",
    'an.esc.impactDesc': "{count} Gespräche erforderten menschliches Eingreifen",
    'an.esc.autoSuccess': "Erfolg der Auto-Lösung",
    'an.esc.autoSuccessDesc': "{count} Gespräche automatisch gelöst",
    'an.esc.note': "Niedrigere Eskalationsraten deuten auf eine bessere KI-Leistung und Lösungsabdeckung hin",
    'an.esc.distribution': "Eskalationsverteilung",
    'an.refreshData': "Daten aktualisieren",
    'an.conf.heading': "KI-Konfidenzanalysen",
    'an.conf.subheading': "Kennzahlen zur Entscheidungskonfidenz der KI und Qualitätsindikatoren",
    'an.conf.exportTitle': "Konfidenzkennzahlen exportieren",
    'an.conf.exportDesc': "Laden Sie die Konfidenzanalysen im gewünschten Format herunter",
    'an.conf.overview': "Konfidenzübersicht",
    'an.conf.averageAiConfidence': "Durchschnittliche KI-Konfidenz",
    'an.conf.highGt80': "Hohe Konfidenz (>80%)",
    'an.conf.metrics': "Konfidenzkennzahlen",
    'an.conf.high': "Hohe Konfidenz",
    'an.conf.above80': "Über 80%",
    'an.conf.medium': "Mittlere Konfidenz",
    'an.conf.range5080': "50-80%",
    'an.conf.low': "Niedrige Konfidenz",
    'an.conf.below50': "Unter 50%",
    'an.conf.conversationsUnit': "Gespräche",
    'an.conf.impact': "Konfidenzauswirkung",
    'an.conf.aiPerfDesc': "Höhere Konfidenzwerte deuten auf ein besseres KI-Training und eine höhere Lösungsqualität hin",
    'an.conf.resolutionQuality': "Lösungsqualität",
    'an.conf.resolutionQualityDesc': "{rate}% der Gespräche mit Konfidenz gelöst",
    'an.conf.note': "Konfidenzwerte spiegeln die Sicherheit der KI bei der Absichtserkennung und Lösungszuordnung wider",
    'an.conf.distribution': "Verteilung der Konfidenzstufen",
    'an.conf.mediumRange': "Mittlere Konfidenz (50-80%)",
    'an.conf.lowLt50': "Niedrige Konfidenz (<50%)",
    'an.conf.bandUnavailable': "Konfidenzbänder pro Konversation werden nicht erfasst – nur die durchschnittliche KI-Konfidenz oben ist verfügbar.",
  },

  it: {
    'an.toast.exportStarted.title': "Esportazione avviata",
    'an.toast.exportStarted.desc': "Generazione del report {format} pronto per il consiglio...",
    'an.toast.exportComplete.title': "Esportazione completata",
    'an.toast.exportComplete.desc': "Il report {format} pronto per il consiglio è stato scaricato",
    'an.toast.exportFailed.title': "Esportazione non riuscita",
    'an.toast.exportFailed.desc': "Impossibile generare il report. Riprova.",
    'an.toast.dateRangeUpdated.title': "Intervallo di date aggiornato",
    'an.toast.dateRangeUpdated.desc': "Visualizzazione delle analisi per il periodo {range}",
    'an.toast.filtersApplied.title': "Filtri applicati",
    'an.toast.filtersApplied.desc': "I dati analitici sono stati filtrati secondo i tuoi criteri",
    'an.toast.filtersReset.title': "Filtri reimpostati",
    'an.toast.filtersReset.desc': "Tutti i filtri sono stati cancellati",
    'an.toast.dataRefreshed.title': "Dati aggiornati",
    'an.toast.refreshFailed.title': "Aggiornamento non riuscito",
    'an.toast.escalationRefreshed.desc': "Dati di escalation aggiornati.",
    'an.toast.escalationRefreshFailed.desc': "Impossibile aggiornare i dati di escalation.",
    'an.toast.confidenceRefreshed.desc': "Dati di confidenza aggiornati.",
    'an.toast.confidenceRefreshFailed.desc': "Impossibile aggiornare i dati di confidenza.",
    'an.sampleBanner': "Al momento non è stato possibile caricare le analisi in tempo reale. Vengono mostrati solo i dati reali disponibili; le cifre potrebbero essere incomplete fino al ripristino della connessione.",
    'an.header.title': "Dashboard analitica",
    'an.header.subtitle': "Approfondimenti completi e metriche di performance",
    'an.dateRange.button': "Intervallo di date",
    'an.dateRange.dialogTitle': "Seleziona intervallo di date",
    'an.dateRange.dialogDesc': "Scegli il periodo di tempo per le analisi",
    'an.dateRange.customRange': "Intervallo personalizzato",
    'an.dateRange.apply': "Applica intervallo",
    'an.dateRange.invalid': "Seleziona una data di inizio e fine. La data di fine non può precedere quella di inizio.",
    'an.dateRange.from': "Dal",
    'an.dateRange.to': "Al",
    'an.period.day': "Oggi",
    'an.period.week': "Questa settimana",
    'an.period.month': "Questo mese",
    'an.period.quarter': "Questo trimestre",
    'an.period.year': "Quest'anno",
    'an.filters.dialogTitle': "Filtri avanzati",
    'an.filters.dialogDesc': "Personalizza la tua vista analitica",
    'an.filters.ticketCategory': "Categoria ticket",
    'an.filters.searchPlaceholder': "Cerca nelle analisi...",
    'an.filters.cat.all': "Tutte le categorie",
    'an.filters.cat.technical': "Supporto tecnico",
    'an.filters.cat.billing': "Fatturazione",
    'an.filters.cat.product': "Problemi di prodotto",
    'an.filters.cat.delivery': "Consegna",
    'an.filters.statusLabel': "Stato",
    'an.filters.status.all': "Tutti gli stati",
    'an.filters.status.open': "Aperto",
    'an.filters.status.inProgress': "In corso",
    'an.filters.status.resolved': "Risolto",
    'an.filters.status.closed': "Chiuso",
    'an.filters.priorityLabel': "Priorità",
    'an.filters.priority.all': "Tutte le priorità",
    'an.filters.priority.low': "Bassa",
    'an.filters.priority.medium': "Media",
    'an.filters.priority.high': "Alta",
    'an.filters.priority.urgent': "Urgente",
    'an.filters.reset': "Reimposta filtri",
    'an.filters.apply': "Applica filtri",
    'an.export.button': "Esporta",
    'an.export.dialogTitle': "Esporta report analitico pronto per il consiglio",
    'an.export.dialogDesc': "Genera report professionali per presentazioni ai dirigenti con grafici, KPI e riepiloghi",
    'an.export.pdf': "PDF esecutivo",
    'an.export.pdfSub': "Report pronto per il consiglio",
    'an.export.excel': "Analisi Excel",
    'an.export.excelSub': "Grafici e tabelle pivot",
    'an.export.ppt': "PowerPoint",
    'an.export.success': 'Esportazione completata',
    'an.aiMetricsUnavailable': 'Le metriche IA non sono disponibili: i dati derivati dall’IA potrebbero essere incompleti. Prova ad aggiornare.',
    'an.export.pptSub': "Diapositive di presentazione",
    'an.export.csv': "CSV dati grezzi",
    'an.export.csvSub': "Analisi dei dati",
    'an.export.generating': "Generazione del report...",
    'an.export.pdfReport': "Report PDF",
    'an.export.excelShort': "Excel",
    'an.export.csvShort': "CSV",
    'an.tab.overview': "Panoramica",
    'an.tab.categories': "Categorie",
    'an.tab.aiVoice': "Voce IA",
    'an.tab.aiAnalytics': "Analisi IA",
    'an.tab.escalation': "Escalation",
    'an.tab.confidence': "Confidenza",
    'an.tab.performance': "Prestazioni",
    'an.tab.customer': "Cliente",
    'an.tab.operational': "Operativo",
    'an.tab.strategic': "Strategico",
    'an.stat.totalConversations': "Conversazioni totali",
    'an.stat.autoResolutionRate': "Tasso di auto-risoluzione",
    'an.stat.avgFirstResponse': "Prima risposta media",
    'an.stat.customerSatisfaction': "Soddisfazione del cliente",
    'an.stat.avgResolutionTime': "Tempo medio di risoluzione",
    'an.stat.avgChatDuration': "Durata media della chat",
    'an.stat.escalationRate': "Tasso di escalation",
    'an.stat.totalTickets': "Ticket totali",
    'an.stat.resolvedTickets': "Ticket risolti",
    'an.stat.reopenedTickets': "Ticket riaperti",
    'an.stat.callbackRate': "Tasso di richiamata",
    'an.stat.reopenedConversations': "Conversazioni riaperte",
    'an.stat.openaiUsageRate': "Tasso di utilizzo di OpenAI",
    'an.stat.totalCalls': "Chiamate totali",
    'an.stat.totalCategories': "Categorie totali",
    'an.stat.resolutionRate': "Tasso di risoluzione",
    'an.label.resolved': "Risolto",
    'an.label.pending': "In attesa",
    'an.label.escalated': "Inoltrato",
    'an.label.tickets': "Ticket",
    'an.label.ticket': "ticket",
    'an.value.noRatings': "Ancora nessuna valutazione",
    'an.value.responses': "{count} risposte",
    'an.chart.weeklyPerformanceTrend': "Andamento delle prestazioni settimanali",
    'an.chart.categoryDistribution': "Distribuzione dei ticket per categoria",
    'an.chart.dailyTicketTrend': "Andamento giornaliero dei ticket",
    'an.chart.ticketsByCategory': "Ticket per categoria",
    'an.chart.conversationIntents': "Intenti di conversazione",
    'an.chart.weeklyResolutionTrends': "Andamenti di risoluzione settimanali",
    'an.chart.topIssues': "Principali problemi di questo periodo",
    'an.chart.categoryResolutionRate': "Tasso di risoluzione per categoria",
    'an.chart.resolutionRateByCategory': "Tasso di risoluzione per categoria",
    'an.chart.categoryBreakdown': "Dettaglio per categoria",
    'an.chart.customerLanguages': "Lingue dei clienti",
    'an.chart.topConversationIntents': "Principali intenti di conversazione",
    'an.chart.dailyTicketVolume': "Volume giornaliero dei ticket",
    'an.chart.resolutionVsEscalation': "Andamento risoluzione vs escalation",
    'an.series.resolutionPct': "Risoluzione %",
    'an.series.escalations': "Escalation",
    'an.empty.noTicketData': "Nessun dato sui ticket nel periodo/filtri selezionati.",
    'an.empty.noTicketActivity': "Nessuna attività sui ticket nel periodo selezionato.",
    'an.empty.noIntentData': "Nessun dato sugli intenti di conversazione ancora disponibile.",
    'an.empty.notEnoughBucket': "Dati insufficienti per raggruppare i tassi di risoluzione.",
    'an.empty.noLanguageData': "Nessun dato sulle lingue ancora disponibile.",
    'an.empty.noTrendData': "Nessun dato di andamento nel periodo selezionato.",
    'an.empty.noCategoryData.title': "Nessun dato di categoria dei ticket per il periodo o i filtri selezionati.",
    'an.empty.noCategoryData.desc': "Quando esisteranno ticket in questo periodo, i dettagli per categoria appariranno qui automaticamente.",
    'an.bucket.excellent': "Eccellente (>90%)",
    'an.bucket.good': "Buono (70-90%)",
    'an.bucket.needsImprovement': "Da migliorare (<70%)",
    'an.it.title': "Le analisi a livello di ticket non sono disponibili per il ruolo IT/Integrazione.",
    'an.it.desc': "Usa le schede Panoramica, Voce IA, Analisi IA, Escalation e Confidenza per le metriche consentite al ruolo IT.",
    'an.table.category': "Categoria",
    'an.table.rate': "Tasso",
    'an.perf.loading': "Le metriche di prestazione in tempo reale si stanno caricando o non sono disponibili per il periodo selezionato.",
    'an.perf.aiPerformance': "Prestazioni IA",
    'an.perf.ticketOperations': "Operazioni sui ticket",
    'an.perf.qualityMetrics': "Metriche di qualità",
    'an.op.loading': "Le metriche operative in tempo reale si stanno caricando o non sono disponibili per il periodo selezionato.",
    'an.strat.loading': "Le metriche strategiche in tempo reale si stanno caricando o non sono disponibili per il periodo selezionato.",
    'an.strat.aiAutoResolution': "Auto-risoluzione IA",
    'an.strat.resolvedWithoutStaff': "Ticket risolti senza personale",
    'an.esc.heading': "Analisi del tasso di escalation",
    'an.esc.subheading': "Metriche e approfondimenti sull'escalation delle conversazioni IA",
    'an.esc.exportTitle': "Esporta metriche di escalation",
    'an.esc.exportDesc': "Scarica le analisi di escalation nel formato che preferisci",
    'an.esc.overview': "Panoramica dell'escalation",
    'an.esc.totalEscalations': "Escalation totali",
    'an.esc.byLevel': "Escalation per livello",
    'an.esc.escalationsCount': "{count} escalation",
    'an.esc.noData': "Nessun dato di escalation disponibile",
    'an.esc.insights': "Approfondimenti sull'escalation",
    'an.esc.impact': "Impatto dell'escalation",
    'an.esc.impactDesc': "{count} conversazioni hanno richiesto l'intervento umano",
    'an.esc.autoSuccess': "Successo dell'auto-risoluzione",
    'an.esc.autoSuccessDesc': "{count} conversazioni risolte automaticamente",
    'an.esc.note': "Tassi di escalation più bassi indicano migliori prestazioni dell'IA e una migliore copertura delle soluzioni",
    'an.esc.distribution': "Distribuzione delle escalation",
    'an.refreshData': "Aggiorna dati",
    'an.conf.heading': "Analisi della confidenza IA",
    'an.conf.subheading': "Metriche di confidenza delle decisioni IA e indicatori di qualità",
    'an.conf.exportTitle': "Esporta metriche di confidenza",
    'an.conf.exportDesc': "Scarica le analisi di confidenza nel formato che preferisci",
    'an.conf.overview': "Panoramica della confidenza",
    'an.conf.averageAiConfidence': "Confidenza media dell'IA",
    'an.conf.highGt80': "Confidenza alta (>80%)",
    'an.conf.metrics': "Metriche di confidenza",
    'an.conf.high': "Confidenza alta",
    'an.conf.above80': "Oltre l'80%",
    'an.conf.medium': "Confidenza media",
    'an.conf.range5080': "50-80%",
    'an.conf.low': "Confidenza bassa",
    'an.conf.below50': "Sotto il 50%",
    'an.conf.conversationsUnit': "conversazioni",
    'an.conf.impact': "Impatto della confidenza",
    'an.conf.aiPerfDesc': "Punteggi di confidenza più alti indicano un migliore addestramento dell'IA e una maggiore qualità delle soluzioni",
    'an.conf.resolutionQuality': "Qualità della risoluzione",
    'an.conf.resolutionQualityDesc': "{rate}% delle conversazioni risolte con confidenza",
    'an.conf.note': "I punteggi di confidenza riflettono la certezza dell'IA nel rilevamento dell'intento e nella corrispondenza delle soluzioni",
    'an.conf.distribution': "Distribuzione dei livelli di confidenza",
    'an.conf.mediumRange': "Confidenza media (50-80%)",
    'an.conf.lowLt50': "Confidenza bassa (<50%)",
    'an.conf.bandUnavailable': "Le fasce di confidenza per conversazione non vengono registrate: è disponibile solo la confidenza media dell'IA mostrata sopra.",
  },

  ja: {
    'an.toast.exportStarted.title': "エクスポートを開始しました",
    'an.toast.exportStarted.desc': "役員向けの{format}レポートを生成しています...",
    'an.toast.exportComplete.title': "エクスポートが完了しました",
    'an.toast.exportComplete.desc': "役員向けの{format}レポートがダウンロードされました",
    'an.toast.exportFailed.title': "エクスポートに失敗しました",
    'an.toast.exportFailed.desc': "レポートの生成に失敗しました。もう一度お試しください。",
    'an.toast.dateRangeUpdated.title': "期間を更新しました",
    'an.toast.dateRangeUpdated.desc': "{range}期間の分析を表示しています",
    'an.toast.filtersApplied.title': "フィルターを適用しました",
    'an.toast.filtersApplied.desc': "分析データが条件に従ってフィルタリングされました",
    'an.toast.filtersReset.title': "フィルターをリセットしました",
    'an.toast.filtersReset.desc': "すべてのフィルターがクリアされました",
    'an.toast.dataRefreshed.title': "データを更新しました",
    'an.toast.refreshFailed.title': "更新に失敗しました",
    'an.toast.escalationRefreshed.desc': "エスカレーションデータを更新しました。",
    'an.toast.escalationRefreshFailed.desc': "エスカレーションデータを更新できませんでした。",
    'an.toast.confidenceRefreshed.desc': "信頼度データを更新しました。",
    'an.toast.confidenceRefreshFailed.desc': "信頼度データを更新できませんでした。",
    'an.sampleBanner': "現在、ライブ分析を読み込めませんでした。利用可能な実データのみを表示しています。接続が回復するまで数値が不完全な場合があります。",
    'an.header.title': "分析ダッシュボード",
    'an.header.subtitle': "包括的なインサイトとパフォーマンス指標",
    'an.dateRange.button': "期間",
    'an.dateRange.dialogTitle': "期間を選択",
    'an.dateRange.dialogDesc': "分析の対象期間を選択してください",
    'an.dateRange.customRange': "カスタム範囲",
    'an.dateRange.apply': "範囲を適用",
    'an.dateRange.invalid': "開始日と終了日を選択してください。終了日は開始日より前にはできません。",
    'an.dateRange.from': "開始",
    'an.dateRange.to': "終了",
    'an.period.day': "今日",
    'an.period.week': "今週",
    'an.period.month': "今月",
    'an.period.quarter': "今四半期",
    'an.period.year': "今年",
    'an.filters.dialogTitle': "詳細フィルター",
    'an.filters.dialogDesc': "分析ビューをカスタマイズします",
    'an.filters.ticketCategory': "チケットカテゴリー",
    'an.filters.searchPlaceholder': "分析を検索...",
    'an.filters.cat.all': "すべてのカテゴリー",
    'an.filters.cat.technical': "テクニカルサポート",
    'an.filters.cat.billing': "請求",
    'an.filters.cat.product': "製品の問題",
    'an.filters.cat.delivery': "配送",
    'an.filters.statusLabel': "ステータス",
    'an.filters.status.all': "すべてのステータス",
    'an.filters.status.open': "オープン",
    'an.filters.status.inProgress': "対応中",
    'an.filters.status.resolved': "解決済み",
    'an.filters.status.closed': "クローズ",
    'an.filters.priorityLabel': "優先度",
    'an.filters.priority.all': "すべての優先度",
    'an.filters.priority.low': "低",
    'an.filters.priority.medium': "中",
    'an.filters.priority.high': "高",
    'an.filters.priority.urgent': "緊急",
    'an.filters.reset': "フィルターをリセット",
    'an.filters.apply': "フィルターを適用",
    'an.export.button': "エクスポート",
    'an.export.dialogTitle': "役員向け分析レポートをエクスポート",
    'an.export.dialogDesc': "グラフ、KPI、要約を含む役員プレゼン用のプロフェッショナルなレポートを生成します",
    'an.export.pdf': "エグゼクティブPDF",
    'an.export.pdfSub': "役員向けレポート",
    'an.export.excel': "Excel分析",
    'an.export.excelSub': "グラフとピボットテーブル",
    'an.export.ppt': "PowerPoint",
    'an.export.success': 'エクスポートが完了しました',
    'an.aiMetricsUnavailable': 'AI指標は現在利用できません。以下のAI由来の数値は不完全な可能性があります。更新してください。',
    'an.export.pptSub': "プレゼンテーションスライド",
    'an.export.csv': "生データCSV",
    'an.export.csvSub': "データ分析",
    'an.export.generating': "レポートを生成しています...",
    'an.export.pdfReport': "PDFレポート",
    'an.export.excelShort': "Excel",
    'an.export.csvShort': "CSV",
    'an.tab.overview': "概要",
    'an.tab.categories': "カテゴリー",
    'an.tab.aiVoice': "AI音声",
    'an.tab.aiAnalytics': "AI分析",
    'an.tab.escalation': "エスカレーション",
    'an.tab.confidence': "信頼度",
    'an.tab.performance': "パフォーマンス",
    'an.tab.customer': "顧客",
    'an.tab.operational': "運用",
    'an.tab.strategic': "戦略",
    'an.stat.totalConversations': "会話総数",
    'an.stat.autoResolutionRate': "自動解決率",
    'an.stat.avgFirstResponse': "平均初回応答",
    'an.stat.customerSatisfaction': "顧客満足度",
    'an.stat.avgResolutionTime': "平均解決時間",
    'an.stat.avgChatDuration': "平均チャット時間",
    'an.stat.escalationRate': "エスカレーション率",
    'an.stat.totalTickets': "チケット総数",
    'an.stat.resolvedTickets': "解決済みチケット",
    'an.stat.reopenedTickets': "再オープンしたチケット",
    'an.stat.callbackRate': "コールバック率",
    'an.stat.reopenedConversations': "再オープンした会話",
    'an.stat.openaiUsageRate': "OpenAI利用率",
    'an.stat.totalCalls': "通話総数",
    'an.stat.totalCategories': "カテゴリー総数",
    'an.stat.resolutionRate': "解決率",
    'an.label.resolved': "解決済み",
    'an.label.pending': "保留中",
    'an.label.escalated': "エスカレーション済み",
    'an.label.tickets': "チケット",
    'an.label.ticket': "チケット",
    'an.value.noRatings': "まだ評価がありません",
    'an.value.responses': "{count}件の回答",
    'an.chart.weeklyPerformanceTrend': "週間パフォーマンス推移",
    'an.chart.categoryDistribution': "カテゴリー別チケット分布",
    'an.chart.dailyTicketTrend': "日次チケット推移",
    'an.chart.ticketsByCategory': "カテゴリー別チケット",
    'an.chart.conversationIntents': "会話の意図",
    'an.chart.weeklyResolutionTrends': "週間解決推移",
    'an.chart.topIssues': "この期間の主な問題",
    'an.chart.categoryResolutionRate': "カテゴリー別解決率",
    'an.chart.resolutionRateByCategory': "カテゴリー別解決率",
    'an.chart.categoryBreakdown': "カテゴリー内訳",
    'an.chart.customerLanguages': "顧客の言語",
    'an.chart.topConversationIntents': "主な会話の意図",
    'an.chart.dailyTicketVolume': "日次チケット量",
    'an.chart.resolutionVsEscalation': "解決対エスカレーションの推移",
    'an.series.resolutionPct': "解決率 %",
    'an.series.escalations': "エスカレーション",
    'an.empty.noTicketData': "選択した期間・フィルターにチケットデータがありません。",
    'an.empty.noTicketActivity': "選択した期間にチケットのアクティビティがありません。",
    'an.empty.noIntentData': "会話の意図データはまだありません。",
    'an.empty.notEnoughBucket': "解決率をグループ化するにはデータが不足しています。",
    'an.empty.noLanguageData': "言語データはまだありません。",
    'an.empty.noTrendData': "選択した期間に推移データがありません。",
    'an.empty.noCategoryData.title': "選択した期間またはフィルターのチケットカテゴリーデータがありません。",
    'an.empty.noCategoryData.desc': "この期間にチケットが存在すると、カテゴリー別の内訳が自動的にここに表示されます。",
    'an.bucket.excellent': "優秀 (>90%)",
    'an.bucket.good': "良好 (70-90%)",
    'an.bucket.needsImprovement': "要改善 (<70%)",
    'an.it.title': "チケットレベルの分析はIT/統合ロールでは利用できません。",
    'an.it.desc': "ITに許可された指標には、概要、AI音声、AI分析、エスカレーション、信頼度のタブをご利用ください。",
    'an.table.category': "カテゴリー",
    'an.table.rate': "率",
    'an.perf.loading': "ライブのパフォーマンス指標を読み込み中か、選択した期間では利用できません。",
    'an.perf.aiPerformance': "AIパフォーマンス",
    'an.perf.ticketOperations': "チケット運用",
    'an.perf.qualityMetrics': "品質指標",
    'an.op.loading': "ライブの運用指標を読み込み中か、選択した期間では利用できません。",
    'an.strat.loading': "ライブの戦略指標を読み込み中か、選択した期間では利用できません。",
    'an.strat.aiAutoResolution': "AI自動解決",
    'an.strat.resolvedWithoutStaff': "スタッフなしで解決したチケット",
    'an.esc.heading': "エスカレーション率分析",
    'an.esc.subheading': "AI会話のエスカレーション指標とインサイト",
    'an.esc.exportTitle': "エスカレーション指標をエクスポート",
    'an.esc.exportDesc': "お好みの形式でエスカレーション分析をダウンロードします",
    'an.esc.overview': "エスカレーション概要",
    'an.esc.totalEscalations': "エスカレーション総数",
    'an.esc.byLevel': "レベル別エスカレーション",
    'an.esc.escalationsCount': "{count}件のエスカレーション",
    'an.esc.noData': "エスカレーションデータがありません",
    'an.esc.insights': "エスカレーションのインサイト",
    'an.esc.impact': "エスカレーションの影響",
    'an.esc.impactDesc': "{count}件の会話で人による対応が必要でした",
    'an.esc.autoSuccess': "自動解決の成功",
    'an.esc.autoSuccessDesc': "{count}件の会話が自動的に解決されました",
    'an.esc.note': "エスカレーション率が低いほど、AIのパフォーマンスとソリューションのカバレッジが優れていることを示します",
    'an.esc.distribution': "エスカレーション分布",
    'an.refreshData': "データを更新",
    'an.conf.heading': "AI信頼度分析",
    'an.conf.subheading': "AI判断の信頼度指標と品質指標",
    'an.conf.exportTitle': "信頼度指標をエクスポート",
    'an.conf.exportDesc': "お好みの形式で信頼度分析をダウンロードします",
    'an.conf.overview': "信頼度概要",
    'an.conf.averageAiConfidence': "平均AI信頼度",
    'an.conf.highGt80': "高信頼度 (>80%)",
    'an.conf.metrics': "信頼度指標",
    'an.conf.high': "高信頼度",
    'an.conf.above80': "80%超",
    'an.conf.medium': "中信頼度",
    'an.conf.range5080': "50-80%",
    'an.conf.low': "低信頼度",
    'an.conf.below50': "50%未満",
    'an.conf.conversationsUnit': "会話",
    'an.conf.impact': "信頼度の影響",
    'an.conf.aiPerfDesc': "信頼度スコアが高いほど、AIのトレーニングとソリューションの品質が優れていることを示します",
    'an.conf.resolutionQuality': "解決の品質",
    'an.conf.resolutionQualityDesc': "会話の{rate}%が信頼度をもって解決されました",
    'an.conf.note': "信頼度スコアは、意図検出とソリューションの照合におけるAIの確信度を反映します",
    'an.conf.distribution': "信頼度レベルの分布",
    'an.conf.mediumRange': "中信頼度 (50-80%)",
    'an.conf.lowLt50': "低信頼度 (<50%)",
    'an.conf.bandUnavailable': "会話ごとの信頼度の内訳は記録されていません。上記の平均AI信頼度のみ利用できます。",
  },

  ko: {
    'an.toast.exportStarted.title': "내보내기 시작됨",
    'an.toast.exportStarted.desc': "이사회용 {format} 보고서를 생성하는 중...",
    'an.toast.exportComplete.title': "내보내기 완료",
    'an.toast.exportComplete.desc': "이사회용 {format} 보고서가 다운로드되었습니다",
    'an.toast.exportFailed.title': "내보내기 실패",
    'an.toast.exportFailed.desc': "보고서 생성에 실패했습니다. 다시 시도해 주세요.",
    'an.toast.dateRangeUpdated.title': "기간이 업데이트됨",
    'an.toast.dateRangeUpdated.desc': "{range} 기간의 분석을 표시하는 중",
    'an.toast.filtersApplied.title': "필터 적용됨",
    'an.toast.filtersApplied.desc': "분석 데이터가 기준에 따라 필터링되었습니다",
    'an.toast.filtersReset.title': "필터 재설정됨",
    'an.toast.filtersReset.desc': "모든 필터가 지워졌습니다",
    'an.toast.dataRefreshed.title': "데이터 새로 고침됨",
    'an.toast.refreshFailed.title': "새로 고침 실패",
    'an.toast.escalationRefreshed.desc': "에스컬레이션 데이터가 업데이트되었습니다.",
    'an.toast.escalationRefreshFailed.desc': "에스컬레이션 데이터를 새로 고칠 수 없습니다.",
    'an.toast.confidenceRefreshed.desc': "신뢰도 데이터가 업데이트되었습니다.",
    'an.toast.confidenceRefreshFailed.desc': "신뢰도 데이터를 새로 고칠 수 없습니다.",
    'an.sampleBanner': "지금은 실시간 분석을 불러올 수 없습니다. 사용 가능한 실제 데이터만 표시됩니다. 연결이 복구될 때까지 수치가 불완전할 수 있습니다.",
    'an.header.title': "분석 대시보드",
    'an.header.subtitle': "종합적인 인사이트와 성과 지표",
    'an.dateRange.button': "기간",
    'an.dateRange.dialogTitle': "기간 선택",
    'an.dateRange.dialogDesc': "분석 대상 기간을 선택하세요",
    'an.dateRange.customRange': "사용자 지정 범위",
    'an.dateRange.apply': "범위 적용",
    'an.dateRange.invalid': "시작일과 종료일을 선택하세요. 종료일은 시작일보다 이전일 수 없습니다.",
    'an.dateRange.from': "시작",
    'an.dateRange.to': "종료",
    'an.period.day': "오늘",
    'an.period.week': "이번 주",
    'an.period.month': "이번 달",
    'an.period.quarter': "이번 분기",
    'an.period.year': "올해",
    'an.filters.dialogTitle': "고급 필터",
    'an.filters.dialogDesc': "분석 보기를 사용자 지정하세요",
    'an.filters.ticketCategory': "티켓 카테고리",
    'an.filters.searchPlaceholder': "분석 검색...",
    'an.filters.cat.all': "모든 카테고리",
    'an.filters.cat.technical': "기술 지원",
    'an.filters.cat.billing': "청구",
    'an.filters.cat.product': "제품 문제",
    'an.filters.cat.delivery': "배송",
    'an.filters.statusLabel': "상태",
    'an.filters.status.all': "모든 상태",
    'an.filters.status.open': "열림",
    'an.filters.status.inProgress': "진행 중",
    'an.filters.status.resolved': "해결됨",
    'an.filters.status.closed': "닫힘",
    'an.filters.priorityLabel': "우선순위",
    'an.filters.priority.all': "모든 우선순위",
    'an.filters.priority.low': "낮음",
    'an.filters.priority.medium': "보통",
    'an.filters.priority.high': "높음",
    'an.filters.priority.urgent': "긴급",
    'an.filters.reset': "필터 재설정",
    'an.filters.apply': "필터 적용",
    'an.export.button': "내보내기",
    'an.export.dialogTitle': "이사회용 분석 보고서 내보내기",
    'an.export.dialogDesc': "차트, KPI, 요약이 포함된 경영진 프레젠테이션용 전문 보고서를 생성합니다",
    'an.export.pdf': "임원용 PDF",
    'an.export.pdfSub': "이사회용 보고서",
    'an.export.excel': "Excel 분석",
    'an.export.excelSub': "차트 및 피벗 테이블",
    'an.export.ppt': "PowerPoint",
    'an.export.success': '내보내기 완료',
    'an.aiMetricsUnavailable': 'AI 지표를 현재 사용할 수 없습니다. 아래 AI 기반 수치가 불완전할 수 있습니다. 새로고침하세요.',
    'an.export.pptSub': "프레젠테이션 슬라이드",
    'an.export.csv': "원시 데이터 CSV",
    'an.export.csvSub': "데이터 분석",
    'an.export.generating': "보고서를 생성하는 중...",
    'an.export.pdfReport': "PDF 보고서",
    'an.export.excelShort': "Excel",
    'an.export.csvShort': "CSV",
    'an.tab.overview': "개요",
    'an.tab.categories': "카테고리",
    'an.tab.aiVoice': "AI 음성",
    'an.tab.aiAnalytics': "AI 분석",
    'an.tab.escalation': "에스컬레이션",
    'an.tab.confidence': "신뢰도",
    'an.tab.performance': "성과",
    'an.tab.customer': "고객",
    'an.tab.operational': "운영",
    'an.tab.strategic': "전략",
    'an.stat.totalConversations': "총 대화 수",
    'an.stat.autoResolutionRate': "자동 해결률",
    'an.stat.avgFirstResponse': "평균 첫 응답",
    'an.stat.customerSatisfaction': "고객 만족도",
    'an.stat.avgResolutionTime': "평균 해결 시간",
    'an.stat.avgChatDuration': "평균 채팅 시간",
    'an.stat.escalationRate': "에스컬레이션률",
    'an.stat.totalTickets': "총 티켓 수",
    'an.stat.resolvedTickets': "해결된 티켓",
    'an.stat.reopenedTickets': "재개된 티켓",
    'an.stat.callbackRate': "콜백률",
    'an.stat.reopenedConversations': "재개된 대화",
    'an.stat.openaiUsageRate': "OpenAI 사용률",
    'an.stat.totalCalls': "총 통화 수",
    'an.stat.totalCategories': "총 카테고리 수",
    'an.stat.resolutionRate': "해결률",
    'an.label.resolved': "해결됨",
    'an.label.pending': "대기 중",
    'an.label.escalated': "에스컬레이션됨",
    'an.label.tickets': "티켓",
    'an.label.ticket': "티켓",
    'an.value.noRatings': "아직 평가 없음",
    'an.value.responses': "{count}개의 응답",
    'an.chart.weeklyPerformanceTrend': "주간 성과 추세",
    'an.chart.categoryDistribution': "카테고리별 티켓 분포",
    'an.chart.dailyTicketTrend': "일별 티켓 추세",
    'an.chart.ticketsByCategory': "카테고리별 티켓",
    'an.chart.conversationIntents': "대화 의도",
    'an.chart.weeklyResolutionTrends': "주간 해결 추세",
    'an.chart.topIssues': "이 기간의 주요 문제",
    'an.chart.categoryResolutionRate': "카테고리별 해결률",
    'an.chart.resolutionRateByCategory': "카테고리별 해결률",
    'an.chart.categoryBreakdown': "카테고리 분석",
    'an.chart.customerLanguages': "고객 언어",
    'an.chart.topConversationIntents': "주요 대화 의도",
    'an.chart.dailyTicketVolume': "일별 티켓 볼륨",
    'an.chart.resolutionVsEscalation': "해결 대 에스컬레이션 추세",
    'an.series.resolutionPct': "해결 %",
    'an.series.escalations': "에스컬레이션",
    'an.empty.noTicketData': "선택한 기간/필터에 티켓 데이터가 없습니다.",
    'an.empty.noTicketActivity': "선택한 기간에 티켓 활동이 없습니다.",
    'an.empty.noIntentData': "아직 대화 의도 데이터가 없습니다.",
    'an.empty.notEnoughBucket': "해결률을 그룹화하기에 데이터가 부족합니다.",
    'an.empty.noLanguageData': "아직 언어 데이터가 없습니다.",
    'an.empty.noTrendData': "선택한 기간에 추세 데이터가 없습니다.",
    'an.empty.noCategoryData.title': "선택한 기간 또는 필터에 대한 티켓 카테고리 데이터가 없습니다.",
    'an.empty.noCategoryData.desc': "이 기간에 티켓이 존재하면 카테고리별 분석이 여기에 자동으로 표시됩니다.",
    'an.bucket.excellent': "우수 (>90%)",
    'an.bucket.good': "양호 (70-90%)",
    'an.bucket.needsImprovement': "개선 필요 (<70%)",
    'an.it.title': "티켓 수준 분석은 IT/통합 역할에서 사용할 수 없습니다.",
    'an.it.desc': "IT에 허용된 지표는 개요, AI 음성, AI 분석, 에스컬레이션 및 신뢰도 탭을 이용하세요.",
    'an.table.category': "카테고리",
    'an.table.rate': "비율",
    'an.perf.loading': "실시간 성과 지표를 불러오는 중이거나 선택한 기간에 사용할 수 없습니다.",
    'an.perf.aiPerformance': "AI 성과",
    'an.perf.ticketOperations': "티켓 운영",
    'an.perf.qualityMetrics': "품질 지표",
    'an.op.loading': "실시간 운영 지표를 불러오는 중이거나 선택한 기간에 사용할 수 없습니다.",
    'an.strat.loading': "실시간 전략 지표를 불러오는 중이거나 선택한 기간에 사용할 수 없습니다.",
    'an.strat.aiAutoResolution': "AI 자동 해결",
    'an.strat.resolvedWithoutStaff': "직원 없이 해결된 티켓",
    'an.esc.heading': "에스컬레이션률 분석",
    'an.esc.subheading': "AI 대화 에스컬레이션 지표 및 인사이트",
    'an.esc.exportTitle': "에스컬레이션 지표 내보내기",
    'an.esc.exportDesc': "원하는 형식으로 에스컬레이션 분석을 다운로드하세요",
    'an.esc.overview': "에스컬레이션 개요",
    'an.esc.totalEscalations': "총 에스컬레이션",
    'an.esc.byLevel': "레벨별 에스컬레이션",
    'an.esc.escalationsCount': "{count}건의 에스컬레이션",
    'an.esc.noData': "에스컬레이션 데이터가 없습니다",
    'an.esc.insights': "에스컬레이션 인사이트",
    'an.esc.impact': "에스컬레이션 영향",
    'an.esc.impactDesc': "{count}건의 대화에 사람의 개입이 필요했습니다",
    'an.esc.autoSuccess': "자동 해결 성공",
    'an.esc.autoSuccessDesc': "{count}건의 대화가 자동으로 해결되었습니다",
    'an.esc.note': "에스컬레이션률이 낮을수록 AI 성과와 솔루션 커버리지가 우수함을 나타냅니다",
    'an.esc.distribution': "에스컬레이션 분포",
    'an.refreshData': "데이터 새로 고침",
    'an.conf.heading': "AI 신뢰도 분석",
    'an.conf.subheading': "AI 의사결정 신뢰도 지표 및 품질 지표",
    'an.conf.exportTitle': "신뢰도 지표 내보내기",
    'an.conf.exportDesc': "원하는 형식으로 신뢰도 분석을 다운로드하세요",
    'an.conf.overview': "신뢰도 개요",
    'an.conf.averageAiConfidence': "평균 AI 신뢰도",
    'an.conf.highGt80': "높은 신뢰도 (>80%)",
    'an.conf.metrics': "신뢰도 지표",
    'an.conf.high': "높은 신뢰도",
    'an.conf.above80': "80% 초과",
    'an.conf.medium': "중간 신뢰도",
    'an.conf.range5080': "50-80%",
    'an.conf.low': "낮은 신뢰도",
    'an.conf.below50': "50% 미만",
    'an.conf.conversationsUnit': "대화",
    'an.conf.impact': "신뢰도 영향",
    'an.conf.aiPerfDesc': "신뢰도 점수가 높을수록 AI 훈련과 솔루션 품질이 우수함을 나타냅니다",
    'an.conf.resolutionQuality': "해결 품질",
    'an.conf.resolutionQualityDesc': "대화의 {rate}%가 신뢰도를 갖고 해결되었습니다",
    'an.conf.note': "신뢰도 점수는 의도 감지 및 솔루션 매칭에 대한 AI의 확신도를 반영합니다",
    'an.conf.distribution': "신뢰도 수준 분포",
    'an.conf.mediumRange': "중간 신뢰도 (50-80%)",
    'an.conf.lowLt50': "낮은 신뢰도 (<50%)",
    'an.conf.bandUnavailable': "대화별 신뢰도 구간은 추적되지 않습니다. 위의 평균 AI 신뢰도만 제공됩니다.",
  },

  zh: {
    'an.toast.exportStarted.title': "已开始导出",
    'an.toast.exportStarted.desc': "正在生成董事会级 {format} 报告...",
    'an.toast.exportComplete.title': "导出完成",
    'an.toast.exportComplete.desc': "董事会级 {format} 报告已下载",
    'an.toast.exportFailed.title': "导出失败",
    'an.toast.exportFailed.desc': "生成报告失败。请重试。",
    'an.toast.dateRangeUpdated.title': "日期范围已更新",
    'an.toast.dateRangeUpdated.desc': "正在显示 {range} 期间的分析",
    'an.toast.filtersApplied.title': "已应用筛选",
    'an.toast.filtersApplied.desc': "分析数据已根据您的条件进行筛选",
    'an.toast.filtersReset.title': "筛选已重置",
    'an.toast.filtersReset.desc': "所有筛选已清除",
    'an.toast.dataRefreshed.title': "数据已刷新",
    'an.toast.refreshFailed.title': "刷新失败",
    'an.toast.escalationRefreshed.desc': "升级数据已更新。",
    'an.toast.escalationRefreshFailed.desc': "无法刷新升级数据。",
    'an.toast.confidenceRefreshed.desc': "置信度数据已更新。",
    'an.toast.confidenceRefreshFailed.desc': "无法刷新置信度数据。",
    'an.sampleBanner': "目前无法加载实时分析。仅显示可用的真实数据；在连接恢复之前，数字可能不完整。",
    'an.header.title': "分析仪表板",
    'an.header.subtitle': "全面的洞察与绩效指标",
    'an.dateRange.button': "日期范围",
    'an.dateRange.dialogTitle': "选择日期范围",
    'an.dateRange.dialogDesc': "选择分析的时间段",
    'an.dateRange.customRange': "自定义范围",
    'an.dateRange.apply': "应用范围",
    'an.dateRange.invalid': "请选择开始和结束日期。结束日期不能早于开始日期。",
    'an.dateRange.from': "从",
    'an.dateRange.to': "至",
    'an.period.day': "今天",
    'an.period.week': "本周",
    'an.period.month': "本月",
    'an.period.quarter': "本季度",
    'an.period.year': "今年",
    'an.filters.dialogTitle': "高级筛选",
    'an.filters.dialogDesc': "自定义您的分析视图",
    'an.filters.ticketCategory': "工单类别",
    'an.filters.searchPlaceholder': "搜索分析...",
    'an.filters.cat.all': "所有类别",
    'an.filters.cat.technical': "技术支持",
    'an.filters.cat.billing': "账单",
    'an.filters.cat.product': "产品问题",
    'an.filters.cat.delivery': "配送",
    'an.filters.statusLabel': "状态",
    'an.filters.status.all': "所有状态",
    'an.filters.status.open': "打开",
    'an.filters.status.inProgress': "处理中",
    'an.filters.status.resolved': "已解决",
    'an.filters.status.closed': "已关闭",
    'an.filters.priorityLabel': "优先级",
    'an.filters.priority.all': "所有优先级",
    'an.filters.priority.low': "低",
    'an.filters.priority.medium': "中",
    'an.filters.priority.high': "高",
    'an.filters.priority.urgent': "紧急",
    'an.filters.reset': "重置筛选",
    'an.filters.apply': "应用筛选",
    'an.export.button': "导出",
    'an.export.dialogTitle': "导出董事会级分析报告",
    'an.export.dialogDesc': "生成包含图表、KPI 和摘要的专业报告，用于高管演示",
    'an.export.pdf': "高管 PDF",
    'an.export.pdfSub': "董事会级报告",
    'an.export.excel': "Excel 分析",
    'an.export.excelSub': "图表和数据透视表",
    'an.export.ppt': "PowerPoint",
    'an.export.success': '导出完成',
    'an.aiMetricsUnavailable': 'AI 指标当前不可用 — 下方由 AI 得出的数字可能不完整。请尝试刷新。',
    'an.export.pptSub': "演示幻灯片",
    'an.export.csv': "原始数据 CSV",
    'an.export.csvSub': "数据分析",
    'an.export.generating': "正在生成报告...",
    'an.export.pdfReport': "PDF 报告",
    'an.export.excelShort': "Excel",
    'an.export.csvShort': "CSV",
    'an.tab.overview': "概览",
    'an.tab.categories': "类别",
    'an.tab.aiVoice': "AI 语音",
    'an.tab.aiAnalytics': "AI 分析",
    'an.tab.escalation': "升级",
    'an.tab.confidence': "置信度",
    'an.tab.performance': "绩效",
    'an.tab.customer': "客户",
    'an.tab.operational': "运营",
    'an.tab.strategic': "战略",
    'an.stat.totalConversations': "对话总数",
    'an.stat.autoResolutionRate': "自动解决率",
    'an.stat.avgFirstResponse': "平均首次响应",
    'an.stat.customerSatisfaction': "客户满意度",
    'an.stat.avgResolutionTime': "平均解决时间",
    'an.stat.avgChatDuration': "平均聊天时长",
    'an.stat.escalationRate': "升级率",
    'an.stat.totalTickets': "工单总数",
    'an.stat.resolvedTickets': "已解决工单",
    'an.stat.reopenedTickets': "重新打开的工单",
    'an.stat.callbackRate': "回拨率",
    'an.stat.reopenedConversations': "重新打开的对话",
    'an.stat.openaiUsageRate': "OpenAI 使用率",
    'an.stat.totalCalls': "通话总数",
    'an.stat.totalCategories': "类别总数",
    'an.stat.resolutionRate': "解决率",
    'an.label.resolved': "已解决",
    'an.label.pending': "待处理",
    'an.label.escalated': "已升级",
    'an.label.tickets': "工单",
    'an.label.ticket': "工单",
    'an.value.noRatings': "尚无评分",
    'an.value.responses': "{count} 条回复",
    'an.chart.weeklyPerformanceTrend': "每周绩效趋势",
    'an.chart.categoryDistribution': "按类别的工单分布",
    'an.chart.dailyTicketTrend': "每日工单趋势",
    'an.chart.ticketsByCategory': "按类别的工单",
    'an.chart.conversationIntents': "对话意图",
    'an.chart.weeklyResolutionTrends': "每周解决趋势",
    'an.chart.topIssues': "本期主要问题",
    'an.chart.categoryResolutionRate': "按类别的解决率",
    'an.chart.resolutionRateByCategory': "按类别的解决率",
    'an.chart.categoryBreakdown': "类别明细",
    'an.chart.customerLanguages': "客户语言",
    'an.chart.topConversationIntents': "主要对话意图",
    'an.chart.dailyTicketVolume': "每日工单量",
    'an.chart.resolutionVsEscalation': "解决与升级趋势",
    'an.series.resolutionPct': "解决率 %",
    'an.series.escalations': "升级",
    'an.empty.noTicketData': "所选期间/筛选中没有工单数据。",
    'an.empty.noTicketActivity': "所选期间没有工单活动。",
    'an.empty.noIntentData': "暂无对话意图数据。",
    'an.empty.notEnoughBucket': "数据不足，无法对解决率进行分组。",
    'an.empty.noLanguageData': "暂无语言数据。",
    'an.empty.noTrendData': "所选期间没有趋势数据。",
    'an.empty.noCategoryData.title': "所选期间或筛选没有工单类别数据。",
    'an.empty.noCategoryData.desc': "一旦此期间存在工单，类别明细将自动显示在此处。",
    'an.bucket.excellent': "优秀 (>90%)",
    'an.bucket.good': "良好 (70-90%)",
    'an.bucket.needsImprovement': "需改进 (<70%)",
    'an.it.title': "IT/集成角色无法使用工单级分析。",
    'an.it.desc': "请使用概览、AI 语音、AI 分析、升级和置信度选项卡查看 IT 允许的指标。",
    'an.table.category': "类别",
    'an.table.rate': "比率",
    'an.perf.loading': "实时绩效指标正在加载或所选期间不可用。",
    'an.perf.aiPerformance': "AI 绩效",
    'an.perf.ticketOperations': "工单运营",
    'an.perf.qualityMetrics': "质量指标",
    'an.op.loading': "实时运营指标正在加载或所选期间不可用。",
    'an.strat.loading': "实时战略指标正在加载或所选期间不可用。",
    'an.strat.aiAutoResolution': "AI 自动解决",
    'an.strat.resolvedWithoutStaff': "无需人工解决的工单",
    'an.esc.heading': "升级率分析",
    'an.esc.subheading': "AI 对话升级指标与洞察",
    'an.esc.exportTitle': "导出升级指标",
    'an.esc.exportDesc': "以您偏好的格式下载升级分析",
    'an.esc.overview': "升级概览",
    'an.esc.totalEscalations': "升级总数",
    'an.esc.byLevel': "按级别的升级",
    'an.esc.escalationsCount': "{count} 次升级",
    'an.esc.noData': "无可用升级数据",
    'an.esc.insights': "升级洞察",
    'an.esc.impact': "升级影响",
    'an.esc.impactDesc': "{count} 个对话需要人工干预",
    'an.esc.autoSuccess': "自动解决成功",
    'an.esc.autoSuccessDesc': "{count} 个对话已自动解决",
    'an.esc.note': "较低的升级率表示更好的 AI 绩效和解决方案覆盖率",
    'an.esc.distribution': "升级分布",
    'an.refreshData': "刷新数据",
    'an.conf.heading': "AI 置信度分析",
    'an.conf.subheading': "AI 决策置信度指标与质量指标",
    'an.conf.exportTitle': "导出置信度指标",
    'an.conf.exportDesc': "以您偏好的格式下载置信度分析",
    'an.conf.overview': "置信度概览",
    'an.conf.averageAiConfidence': "平均 AI 置信度",
    'an.conf.highGt80': "高置信度 (>80%)",
    'an.conf.metrics': "置信度指标",
    'an.conf.high': "高置信度",
    'an.conf.above80': "80% 以上",
    'an.conf.medium': "中等置信度",
    'an.conf.range5080': "50-80%",
    'an.conf.low': "低置信度",
    'an.conf.below50': "50% 以下",
    'an.conf.conversationsUnit': "对话",
    'an.conf.impact': "置信度影响",
    'an.conf.aiPerfDesc': "较高的置信度分数表示更好的 AI 训练和解决方案质量",
    'an.conf.resolutionQuality': "解决质量",
    'an.conf.resolutionQualityDesc': "{rate}% 的对话以置信度解决",
    'an.conf.note': "置信度分数反映了 AI 在意图检测和解决方案匹配方面的确定性",
    'an.conf.distribution': "置信度水平分布",
    'an.conf.mediumRange': "中等置信度 (50-80%)",
    'an.conf.lowLt50': "低置信度 (<50%)",
    'an.conf.bandUnavailable': "系统未记录每次会话的置信度区间，仅提供上方的平均AI置信度。",
  },

  ar: {
    'an.toast.exportStarted.title': "بدأ التصدير",
    'an.toast.exportStarted.desc': "جارٍ إنشاء تقرير {format} جاهز للمجلس...",
    'an.toast.exportComplete.title': "اكتمل التصدير",
    'an.toast.exportComplete.desc': "تم تنزيل تقرير {format} الجاهز للمجلس",
    'an.toast.exportFailed.title': "فشل التصدير",
    'an.toast.exportFailed.desc': "تعذّر إنشاء التقرير. يرجى المحاولة مرة أخرى.",
    'an.toast.dateRangeUpdated.title': "تم تحديث نطاق التاريخ",
    'an.toast.dateRangeUpdated.desc': "عرض التحليلات لفترة {range}",
    'an.toast.filtersApplied.title': "تم تطبيق الفلاتر",
    'an.toast.filtersApplied.desc': "تمت تصفية بيانات التحليلات وفقًا لمعاييرك",
    'an.toast.filtersReset.title': "تمت إعادة تعيين الفلاتر",
    'an.toast.filtersReset.desc': "تم مسح جميع الفلاتر",
    'an.toast.dataRefreshed.title': "تم تحديث البيانات",
    'an.toast.refreshFailed.title': "فشل التحديث",
    'an.toast.escalationRefreshed.desc': "تم تحديث بيانات التصعيد.",
    'an.toast.escalationRefreshFailed.desc': "تعذّر تحديث بيانات التصعيد.",
    'an.toast.confidenceRefreshed.desc': "تم تحديث بيانات الثقة.",
    'an.toast.confidenceRefreshFailed.desc': "تعذّر تحديث بيانات الثقة.",
    'an.sampleBanner': "تعذّر تحميل التحليلات المباشرة في الوقت الحالي. يتم عرض البيانات الحقيقية المتاحة فقط؛ قد تكون الأرقام غير مكتملة حتى يُستعاد الاتصال.",
    'an.header.title': "لوحة معلومات التحليلات",
    'an.header.subtitle': "رؤى شاملة ومقاييس أداء",
    'an.dateRange.button': "نطاق التاريخ",
    'an.dateRange.dialogTitle': "اختيار نطاق التاريخ",
    'an.dateRange.dialogDesc': "اختر الفترة الزمنية للتحليلات",
    'an.dateRange.customRange': "نطاق مخصص",
    'an.dateRange.apply': "تطبيق النطاق",
    'an.dateRange.invalid': "حدد تاريخ البداية والنهاية. لا يمكن أن يسبق تاريخ النهاية تاريخ البداية.",
    'an.dateRange.from': "من",
    'an.dateRange.to': "إلى",
    'an.period.day': "اليوم",
    'an.period.week': "هذا الأسبوع",
    'an.period.month': "هذا الشهر",
    'an.period.quarter': "هذا الربع",
    'an.period.year': "هذه السنة",
    'an.filters.dialogTitle': "فلاتر متقدمة",
    'an.filters.dialogDesc': "خصّص عرض التحليلات الخاص بك",
    'an.filters.ticketCategory': "فئة التذكرة",
    'an.filters.searchPlaceholder': "البحث في التحليلات...",
    'an.filters.cat.all': "جميع الفئات",
    'an.filters.cat.technical': "الدعم الفني",
    'an.filters.cat.billing': "الفوترة",
    'an.filters.cat.product': "مشكلات المنتج",
    'an.filters.cat.delivery': "التوصيل",
    'an.filters.statusLabel': "الحالة",
    'an.filters.status.all': "جميع الحالات",
    'an.filters.status.open': "مفتوح",
    'an.filters.status.inProgress': "قيد التنفيذ",
    'an.filters.status.resolved': "محلول",
    'an.filters.status.closed': "مغلق",
    'an.filters.priorityLabel': "الأولوية",
    'an.filters.priority.all': "جميع الأولويات",
    'an.filters.priority.low': "منخفضة",
    'an.filters.priority.medium': "متوسطة",
    'an.filters.priority.high': "عالية",
    'an.filters.priority.urgent': "عاجلة",
    'an.filters.reset': "إعادة تعيين الفلاتر",
    'an.filters.apply': "تطبيق الفلاتر",
    'an.export.button': "تصدير",
    'an.export.dialogTitle': "تصدير تقرير تحليلات جاهز للمجلس",
    'an.export.dialogDesc': "أنشئ تقارير احترافية لعروض الإدارة التنفيذية تتضمن رسومًا بيانية ومؤشرات أداء وملخصات",
    'an.export.pdf': "PDF تنفيذي",
    'an.export.pdfSub': "تقرير جاهز للمجلس",
    'an.export.excel': "تحليل Excel",
    'an.export.excelSub': "رسوم بيانية وجداول محورية",
    'an.export.ppt': "PowerPoint",
    'an.export.success': 'اكتمل التصدير',
    'an.aiMetricsUnavailable': 'مقاييس الذكاء الاصطناعي غير متاحة حاليًا — قد تكون الأرقام المشتقة أدناه غير مكتملة. حاول التحديث.',
    'an.export.pptSub': "شرائح العرض التقديمي",
    'an.export.csv': "بيانات خام CSV",
    'an.export.csvSub': "تحليل البيانات",
    'an.export.generating': "جارٍ إنشاء التقرير...",
    'an.export.pdfReport': "تقرير PDF",
    'an.export.excelShort': "Excel",
    'an.export.csvShort': "CSV",
    'an.tab.overview': "نظرة عامة",
    'an.tab.categories': "الفئات",
    'an.tab.aiVoice': "صوت الذكاء الاصطناعي",
    'an.tab.aiAnalytics': "تحليلات الذكاء الاصطناعي",
    'an.tab.escalation': "التصعيد",
    'an.tab.confidence': "الثقة",
    'an.tab.performance': "الأداء",
    'an.tab.customer': "العميل",
    'an.tab.operational': "التشغيلي",
    'an.tab.strategic': "الاستراتيجي",
    'an.stat.totalConversations': "إجمالي المحادثات",
    'an.stat.autoResolutionRate': "معدل الحل التلقائي",
    'an.stat.avgFirstResponse': "متوسط الاستجابة الأولى",
    'an.stat.customerSatisfaction': "رضا العملاء",
    'an.stat.avgResolutionTime': "متوسط وقت الحل",
    'an.stat.avgChatDuration': "متوسط مدة الدردشة",
    'an.stat.escalationRate': "معدل التصعيد",
    'an.stat.totalTickets': "إجمالي التذاكر",
    'an.stat.resolvedTickets': "التذاكر المحلولة",
    'an.stat.reopenedTickets': "التذاكر المعاد فتحها",
    'an.stat.callbackRate': "معدل معاودة الاتصال",
    'an.stat.reopenedConversations': "المحادثات المعاد فتحها",
    'an.stat.openaiUsageRate': "معدل استخدام OpenAI",
    'an.stat.totalCalls': "إجمالي المكالمات",
    'an.stat.totalCategories': "إجمالي الفئات",
    'an.stat.resolutionRate': "معدل الحل",
    'an.label.resolved': "محلول",
    'an.label.pending': "قيد الانتظار",
    'an.label.escalated': "مُصعّد",
    'an.label.tickets': "تذاكر",
    'an.label.ticket': "تذكرة",
    'an.value.noRatings': "لا توجد تقييمات بعد",
    'an.value.responses': "{count} ردود",
    'an.chart.weeklyPerformanceTrend': "اتجاه الأداء الأسبوعي",
    'an.chart.categoryDistribution': "توزيع التذاكر حسب الفئة",
    'an.chart.dailyTicketTrend': "اتجاه التذاكر اليومي",
    'an.chart.ticketsByCategory': "التذاكر حسب الفئة",
    'an.chart.conversationIntents': "نوايا المحادثة",
    'an.chart.weeklyResolutionTrends': "اتجاهات الحل الأسبوعية",
    'an.chart.topIssues': "أبرز المشكلات في هذه الفترة",
    'an.chart.categoryResolutionRate': "معدل الحل حسب الفئة",
    'an.chart.resolutionRateByCategory': "معدل الحل حسب الفئة",
    'an.chart.categoryBreakdown': "تفصيل الفئات",
    'an.chart.customerLanguages': "لغات العملاء",
    'an.chart.topConversationIntents': "أبرز نوايا المحادثة",
    'an.chart.dailyTicketVolume': "حجم التذاكر اليومي",
    'an.chart.resolutionVsEscalation': "اتجاه الحل مقابل التصعيد",
    'an.series.resolutionPct': "الحل %",
    'an.series.escalations': "التصعيدات",
    'an.empty.noTicketData': "لا توجد بيانات تذاكر في الفترة/الفلاتر المحددة.",
    'an.empty.noTicketActivity': "لا يوجد نشاط تذاكر في الفترة المحددة.",
    'an.empty.noIntentData': "لا تتوفر بيانات نوايا المحادثة بعد.",
    'an.empty.notEnoughBucket': "لا توجد بيانات كافية لتجميع معدلات الحل.",
    'an.empty.noLanguageData': "لا تتوفر بيانات لغة بعد.",
    'an.empty.noTrendData': "لا توجد بيانات اتجاه في الفترة المحددة.",
    'an.empty.noCategoryData.title': "لا توجد بيانات فئة تذاكر للفترة أو الفلاتر المحددة.",
    'an.empty.noCategoryData.desc': "بمجرد وجود تذاكر في هذه الفترة، ستظهر تفصيلات الفئات هنا تلقائيًا.",
    'an.bucket.excellent': "ممتاز (>90%)",
    'an.bucket.good': "جيد (70-90%)",
    'an.bucket.needsImprovement': "يحتاج إلى تحسين (<70%)",
    'an.it.title': "التحليلات على مستوى التذاكر غير متاحة لدور تقنية المعلومات/التكامل.",
    'an.it.desc': "استخدم علامات التبويب نظرة عامة، وصوت الذكاء الاصطناعي، وتحليلات الذكاء الاصطناعي، والتصعيد، والثقة للمقاييس المسموح بها لتقنية المعلومات.",
    'an.table.category': "الفئة",
    'an.table.rate': "المعدل",
    'an.perf.loading': "مقاييس الأداء المباشرة قيد التحميل أو غير متاحة للفترة المحددة.",
    'an.perf.aiPerformance': "أداء الذكاء الاصطناعي",
    'an.perf.ticketOperations': "عمليات التذاكر",
    'an.perf.qualityMetrics': "مقاييس الجودة",
    'an.op.loading': "المقاييس التشغيلية المباشرة قيد التحميل أو غير متاحة للفترة المحددة.",
    'an.strat.loading': "المقاييس الاستراتيجية المباشرة قيد التحميل أو غير متاحة للفترة المحددة.",
    'an.strat.aiAutoResolution': "الحل التلقائي بالذكاء الاصطناعي",
    'an.strat.resolvedWithoutStaff': "التذاكر المحلولة بدون موظفين",
    'an.esc.heading': "تحليلات معدل التصعيد",
    'an.esc.subheading': "مقاييس ورؤى تصعيد محادثات الذكاء الاصطناعي",
    'an.esc.exportTitle': "تصدير مقاييس التصعيد",
    'an.esc.exportDesc': "قم بتنزيل تحليلات التصعيد بالتنسيق الذي تفضّله",
    'an.esc.overview': "نظرة عامة على التصعيد",
    'an.esc.totalEscalations': "إجمالي التصعيدات",
    'an.esc.byLevel': "التصعيد حسب المستوى",
    'an.esc.escalationsCount': "{count} تصعيدات",
    'an.esc.noData': "لا تتوفر بيانات تصعيد",
    'an.esc.insights': "رؤى التصعيد",
    'an.esc.impact': "أثر التصعيد",
    'an.esc.impactDesc': "{count} محادثات تطلّبت تدخلًا بشريًا",
    'an.esc.autoSuccess': "نجاح الحل التلقائي",
    'an.esc.autoSuccessDesc': "{count} محادثات تم حلها تلقائيًا",
    'an.esc.note': "معدلات التصعيد المنخفضة تشير إلى أداء أفضل للذكاء الاصطناعي وتغطية أفضل للحلول",
    'an.esc.distribution': "توزيع التصعيد",
    'an.refreshData': "تحديث البيانات",
    'an.conf.heading': "تحليلات ثقة الذكاء الاصطناعي",
    'an.conf.subheading': "مقاييس ثقة قرارات الذكاء الاصطناعي ومؤشرات الجودة",
    'an.conf.exportTitle': "تصدير مقاييس الثقة",
    'an.conf.exportDesc': "قم بتنزيل تحليلات الثقة بالتنسيق الذي تفضّله",
    'an.conf.overview': "نظرة عامة على الثقة",
    'an.conf.averageAiConfidence': "متوسط ثقة الذكاء الاصطناعي",
    'an.conf.highGt80': "ثقة عالية (>80%)",
    'an.conf.metrics': "مقاييس الثقة",
    'an.conf.high': "ثقة عالية",
    'an.conf.above80': "أعلى من 80%",
    'an.conf.medium': "ثقة متوسطة",
    'an.conf.range5080': "50-80%",
    'an.conf.low': "ثقة منخفضة",
    'an.conf.below50': "أقل من 50%",
    'an.conf.conversationsUnit': "محادثات",
    'an.conf.impact': "أثر الثقة",
    'an.conf.aiPerfDesc': "درجات الثقة الأعلى تشير إلى تدريب أفضل للذكاء الاصطناعي وجودة أعلى للحلول",
    'an.conf.resolutionQuality': "جودة الحل",
    'an.conf.resolutionQualityDesc': "{rate}% من المحادثات تم حلها بثقة",
    'an.conf.note': "تعكس درجات الثقة يقين الذكاء الاصطناعي في اكتشاف النية ومطابقة الحلول",
    'an.conf.distribution': "توزيع مستوى الثقة",
    'an.conf.mediumRange': "ثقة متوسطة (50-80%)",
    'an.conf.lowLt50': "ثقة منخفضة (<50%)",
    'an.conf.bandUnavailable': "لا يتم تتبع نطاقات الثقة لكل محادثة — يتوفر فقط متوسط ثقة الذكاء الاصطناعي أعلاه.",
  },
};

export default analyticsTranslations;
